var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/regions/regions.ts
var DRAWER_REGION_ID_RIGHT = "app-global-drawer-region-right";
var DRAWER_REGION_ID_LEFT = "app-global-drawer-region-left";

// src/utils/persistent-storage-service.ts
var DEFAULT_STORAGE = sessionStorage;
var saveItem = (key, value, storage = DEFAULT_STORAGE) => {
  try {
    const serializedValue = JSON.stringify(value);
    storage.setItem(key, serializedValue);
  } catch (error) {
    console.error(`Failed to save item with key "${key}":`, error);
  }
};
var loadItem = (key, storage = DEFAULT_STORAGE) => {
  try {
    const serializedValue = storage.getItem(key);
    if (serializedValue === null) {
      return void 0;
    }
    return JSON.parse(serializedValue);
  } catch (error) {
    console.error(`Failed to load item with key "${key}":`, error);
    return void 0;
  }
};
var removeItem = (key, storage = DEFAULT_STORAGE) => {
  try {
    storage.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove item with key "${key}":`, error);
  }
};
var persistentStorageService = {
  saveItem,
  loadItem,
  removeItem
};

// src/hooks/outside-click-hook.tsx
import { useEffect, useRef } from "react";
var useOutsideClickHook = (callback) => {
  const ref = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [callback]);
  return ref;
};

// src/hooks/scroll-cache-hook.ts
import { useRef as useRef2 } from "react";
var useScrollCacheHook = (cacheKey, storage = sessionStorage) => {
  const manuallyScrollToElementRef = useRef2(null);
  const scrollToSavedCache = () => {
    if (manuallyScrollToElementRef.current) {
      try {
        const cache = persistentStorageService.loadItem(cacheKey, storage);
        if (cache) {
          manuallyScrollToElementRef.current.scrollTo({
            top: cache.top,
            left: 0,
            behavior: "auto"
          });
        }
      } catch (error) {
        console.error(`Failed to scroll to saved cache:`, error);
      }
    }
  };
  const saveCurrentPositionToCache = (event) => {
    persistentStorageService.saveItem(cacheKey, {
      top: event.currentTarget.scrollTop
    });
  };
  const resetScrollCache = () => {
    persistentStorageService.saveItem(cacheKey, { top: 0 });
  };
  return {
    manuallyScrollToElementRef,
    scrollToSavedCache,
    saveCurrentPositionToCache,
    resetScrollCache
  };
};

// src/hooks/disable-input-scroll-hook.ts
import { useEffect as useEffect2, useState } from "react";
var useDisableInputScrollHook = (inputRef) => {
  const [input, setInput] = useState(inputRef.current);
  useEffect2(() => {
    if (!input) {
      return;
    }
    const handleWheel = (e) => {
      e.preventDefault();
    };
    input.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      input.removeEventListener("wheel", handleWheel);
    };
  }, [input]);
  const setRef = (ref) => {
    setInput(ref.current);
  };
  return {
    setRef
  };
};

// src/hooks/focus-hook.tsx
import { useRef as useRef3 } from "react";
function useFocusHook() {
  const htmlElRef = useRef3(null);
  const setFocus = () => {
    setTimeout(() => {
      if (htmlElRef.current !== null) {
        htmlElRef.current.focus();
      }
    }, 1);
  };
  return [htmlElRef, setFocus];
}

// src/hooks/key-press-hook.ts
import { useState as useState2, useEffect as useEffect3 } from "react";
function useKeyPressHook({
  key,
  onKeyPressed
}) {
  const [keyPressed, setKeyPressed] = useState2(false);
  useEffect3(() => {
    function keyDownHandler(e) {
      if (e.key === key) {
        e.preventDefault();
        if (onKeyPressed) {
          onKeyPressed();
        }
        setKeyPressed(true);
      }
    }
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [key, onKeyPressed]);
  return { keyPressed };
}

// src/hooks/debounce-hook.ts
import { useState as useState3, useEffect as useEffect4 } from "react";
function useDebounceHook(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState3(value);
  useEffect4(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

// src/components/badge/badge-component.tsx
import styled from "styled-components";
var badgeVariants = {
  primary: "primary",
  secondary: "secondary"
};
var getBadgeComponentBorderLeft = (borderLeft, borderLeftColor) => {
  if (borderLeft) {
    if (typeof borderLeft === "boolean") {
      if (borderLeftColor) {
        return `5px solid ${borderLeftColor}`;
      }
      return "5px solid var(--otk-ui-badge-border-left-color-default)";
    }
    return `5px solid var(--otk-ui-badge-border-left-color-${borderLeft})`;
  }
  return "unset";
};
var BadgeComponent = styled.div`
  display: inline-block;
  font-family: var(--otk-ui-badge-font-family, sans-serif);
  font-weight: var(--otk-ui-badge-font-weight, 700);
  font-size: var(--otk-ui-badge-font-size, 12px);
  padding: ${({ padding }) => padding ? padding : "var(--otk-ui-badge-padding, 2px 6px)"};
  border-radius: ${({ borderRadius }) => borderRadius ? borderRadius : "var(--otk-ui-badge-border-radius, 10px)"};
  border-left: ${({ borderLeft, borderLeftColor }) => getBadgeComponentBorderLeft(borderLeft, borderLeftColor)};
  color: ${({ variant }) => {
  if (variant === badgeVariants.primary) {
    return "var(--otk-ui-badge-primary-color)";
  }
  if (variant === badgeVariants.secondary) {
    return "var(--otk-ui-badge-secondary-color)";
  }
  return "var(--otk-ui-badge-color)";
}};
  background-color: ${({ variant }) => {
  if (variant === badgeVariants.primary) {
    return "var(--otk-ui-badge-primary-background-color)";
  }
  if (variant === badgeVariants.secondary) {
    return "var(--otk-ui-badge-secondary-background-color)";
  }
  return "var(--otk-ui-badge-background-color)";
}};
  ${({ color }) => color && `color: ${color};`}
  ${({ backgroundColor }) => backgroundColor && `background-color: ${backgroundColor};`}
`;

// src/components/block.tsx
import styled2 from "styled-components";
var Block = styled2.div`
  display: flex;
  width: 600px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

// src/components/block-container.tsx
import styled3 from "styled-components";
var BlockContainer = styled3.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  justify-content: center;
  padding-top: 32px;
  padding-bottom: 32px;
`;

// src/components/accordion/accordion-component.tsx
import React4 from "react";
import styled7 from "styled-components";

// src/components/accordion/accordion-content-component.tsx
import React from "react";
import styled4 from "styled-components";
var AccordionContentRootComponent = styled4.div`
  interpolate-size: allow-keywords;
  width: 100%;
  background-color: transparent;
  padding: ${({ padded }) => padded ? "var(--otk-ui-accordion-content-padding, 8px 16px)" : "0"};
  transition:
    height 0.5s ease,
    opacity 0.9s ease;

  opacity: ${({ open }) => {
  if (open) {
    return 1;
  }
  return 0;
}};

  height: ${({ open }) => {
  if (open) {
    return "auto";
  }
  return "0";
}};

  overflow-y: ${({ open }) => {
  if (open) {
    return "clip";
  }
  return "hidden";
}};

  @starting-style {
    opacity: 0;
    height: 0;
  }
`;
function AccordionContentComponent({
  children,
  open = false,
  padded = false
}) {
  return /* @__PURE__ */ React.createElement(AccordionContentRootComponent, { open, padded }, children);
}

// src/components/accordion/accordion-icon-component.tsx
import React2 from "react";
import styled5 from "styled-components";
import { ArrowRightIcon } from "@ontruck/fe-icons";
var AccordionIconWrapperComponent = styled5.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 100ms linear all;
  transform-origin: center;
  transform: ${({ open }) => open && "rotateZ(90deg)"};

  svg {
    path {
      fill: var(--otk-ui-accordion-icon-fill, black);
    }
  }
`;
function AccordionIconComponent({
  icon = /* @__PURE__ */ React2.createElement(ArrowRightIcon, { sizeInPixels: 7 }),
  open
}) {
  return /* @__PURE__ */ React2.createElement(AccordionIconWrapperComponent, { open }, icon);
}

// src/components/accordion/accordion-trigger-component.tsx
import React3 from "react";
import styled6 from "styled-components";
var AccordionTriggerRootComponent = styled6.header`
  width: 100%;
  height: auto;
  background-color: transparent;
  border-bottom: ${({ open }) => open ? "var(--otk-ui-accordion-trigger-border-bottom)" : "none"};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;
function AccordionTriggerComponent({
  className,
  children,
  onClick,
  open
}) {
  return /* @__PURE__ */ React3.createElement(AccordionTriggerRootComponent, { className, onClick, open }, children);
}

// src/components/accordion/accordion-component.tsx
var AccordionRootComponent = styled7.div`
  width: 100%;
  height: auto;
  background-color: ${({ backgroundColor }) => backgroundColor || "var(--otk-ui-accordion-background-color)"};
  border: ${({ border }) => border || "var(--otk-ui-accordion-border)"};
`;
function AccordionComponent({
  children,
  backgroundColor,
  border
}) {
  return /* @__PURE__ */ React4.createElement(AccordionRootComponent, { backgroundColor, border }, children);
}
AccordionComponent.Content = AccordionContentComponent;
AccordionComponent.Icon = AccordionIconComponent;
AccordionComponent.Trigger = AccordionTriggerComponent;

// src/components/avatar/avatar-component.tsx
import React5 from "react";
import styled8 from "styled-components";
var AvatarContainerComponent = styled8.div`
  display: ${({ display }) => display || "flex"};
  width: ${({ width }) => width || "auto"};
  justify-content: center;
  align-items: center;
  gap: var(--otk-ui-avatar-container-gap, 10px);
  align-self: stretch;
  border-left: ${({ showBorder }) => showBorder ? "2px solid white" : "none"};
  border-right: ${({ showBorder }) => showBorder ? "2px solid white" : "none"};
  margin: ${({ margin }) => margin || "0"};
`;
var AvatarWrapperComponent = styled8.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--otk-ui-avatar-wrapper-size, 32px);
  height: var(--otk-ui-avatar-wrapper-size, 32px);
  cursor: default;
`;
var AvatarLettersComponent = styled8.div`
  display: flex;
  width: var(--otk-ui-avatar-letters-size, 32px);
  height: var(--otk-ui-avatar-letters-size, 32px);
  align-items: center;
  justify-content: center;
  margin: auto;
  font-size: var(--otk-ui-avatar-letters-font-size, 14px);
  font-weight: var(--otk-ui-avatar-letters-font-weight, 700);
  background: ${({ background }) => background || "var(--otk-ui-avatar-background, #94A6B8)"};
  color: var(--otk-ui-avatar-letters-color, #ffffff);
  border-radius: 50%;
`;
function AvatarComponent({
  backgroundColor,
  initials,
  showBorder = true,
  display = "flex",
  margin = "0",
  width = "49px"
}) {
  return /* @__PURE__ */ React5.createElement(
    AvatarContainerComponent,
    {
      display,
      margin,
      showBorder,
      width
    },
    /* @__PURE__ */ React5.createElement(AvatarWrapperComponent, null, /* @__PURE__ */ React5.createElement(AvatarLettersComponent, { background: backgroundColor }, initials))
  );
}

// src/components/avatar/get-avatar-initials-by-user-service.ts
var getAvatarInitialsByUserService = (user) => {
  if (user.firstName.trim().length > 0 && user.lastName.trim().length > 0) {
    return `${user.firstName[0]}${user.lastName[0]}`;
  }
  if (user.username.trim().length > 0) {
    return `${user.username.charAt(0).toUpperCase()}${user.username.charAt(1)}`;
  }
  return "??";
};

// src/components/avatar/get-avatar-background-by-user-service.ts
var getAvatarBackgroundByUserService = (user) => {
  const username = user.username;
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + hash * 31;
  }
  const h = hash % 360;
  return `hsl(${h}, 30%, 50%)`;
};

// src/components/ball/ball-component.tsx
import styled9 from "styled-components";
var BallComponent = styled9.span`
  width: ${({ size }) => size ? size : "var(--otk-ui-ball-size, 10px)"};
  height: ${({ size }) => size ? size : "var(--otk-ui-ball-size, 10px)"};
  border-radius: 50%;
  display: inline-block;
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor : "var(--otk-ui-ball-background-color, red)"};
`;

// src/components/labeled-ball/labeled-ball-component.tsx
import React6 from "react";
import styled13 from "styled-components";

// src/components/flex/flex-component.tsx
import styled10 from "styled-components";
var FlexComponent = styled10.div`
  align-items: ${({ alignItems }) => alignItems || "flex-start"};
  align-self: ${({ alignSelf }) => alignSelf || "auto"};
  background: ${({ backgroundColor }) => backgroundColor || "transparent"};
  border-bottom: ${({ borderBottom }) => borderBottom || "none"};
  border-left: ${({ borderLeft }) => borderLeft || "transparent"};
  border-right: ${({ borderRight }) => borderRight || "transparent"};
  border-top: ${({ borderTop }) => borderTop || "none"};
  bottom: ${({ bottom }) => bottom || "unset"};
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection || "row"};
  flex-grow: ${({ flexGrow }) => flexGrow || "0"};
  flex-wrap: ${({ flexWrap }) => flexWrap || "nowrap"};
  flex: ${({ flex }) => flex || "unset"};
  gap: ${({ gap }) => gap || "0"};
  height: ${({ height }) => height || "auto"};
  justify-content: ${({ justifyContent }) => justifyContent || "flex-start"};
  margin-right: ${({ marginRight }) => marginRight || "0"};
  margin: ${({ margin }) => margin || "0"};
  max-width: ${({ maxWidth }) => maxWidth || "unset"};
  min-height: ${({ minHeight }) => minHeight || "unset"};
  overflow: ${({ overflow }) => overflow || "unset"};
  overflow-x: ${({ overflowX, overflow }) => overflowX && !overflow ? overflowX : "unset"};
  overflow-y: ${({ overflowY, overflow }) => overflowY && !overflow ? overflowY : "unset"};
  padding: ${({ padding }) => padding || "0"};
  position: ${({ position }) => position || "relative"};
  top: ${({ top }) => top || "unset"};
  width: ${({ width }) => width || "auto"};
`;

// src/components/text/text-component.tsx
import styled11 from "styled-components";
var textSizes = {
  xxs: "2xs",
  xs: "xs",
  s: "s",
  m: "m",
  l: "l",
  xl: "xl",
  xxl: "2xl",
  xxxl: "3xl",
  xxxxl: "4xl"
};
var textWeights = {
  light: "light",
  regular: "regular",
  medium: "medium",
  bold: "bold"
};
var textLineHeights = {
  xxs: "2xs",
  xs: "xs",
  s: "s",
  m: "m",
  l: "l",
  xl: "xl",
  xxl: "2xl",
  xxxl: "3xl",
  xxxxl: "4xl"
};
var textVariants = {
  light: "light",
  primary: "primary",
  secondary: "secondary"
};
var TextComponent = styled11.div`
  font-family: var(--otk-theme-font-family-primary, sans-serif);

  color: ${({ color, variant }) => {
  if (color) {
    return color;
  }
  if (variant === textVariants.light) {
    return "var(--otk-ui-text-color-light, #fff)";
  }
  if (variant === textVariants.primary) {
    return "var(--otk-ui-text-color-primary, #39a0df)";
  }
  if (variant === textVariants.secondary) {
    return "var(--otk-ui-text-color-secondary, #54708C)";
  }
  return "var(--otk-ui-text-color, #54708C)";
}};

  font-size: ${({ fontSize, size }) => {
  if (fontSize) {
    return fontSize;
  }
  if (size === textSizes.xxs) {
    return "var(--otk-theme-font-size-2xs, 8px)";
  }
  if (size === textSizes.xs) {
    return "var(--otk-theme-font-size-xs, 10px)";
  }
  if (size === textSizes.s) {
    return "var(--otk-theme-font-size-s, 12px)";
  }
  if (size === textSizes.m) {
    return "var(--otk-theme-font-size-m, 14px)";
  }
  if (size === textSizes.l) {
    return "var(--otk-theme-font-size-l, 16px)";
  }
  if (size === textSizes.xl) {
    return "var(--otk-theme-font-size-xl, 18px)";
  }
  if (size === textSizes.xxl) {
    return "var(--otk-theme-font-size-2xl, 20px)";
  }
  if (size === textSizes.xxxl) {
    return "var(--otk-theme-font-size-3xl, 24px)";
  }
  if (size === textSizes.xxxxl) {
    return "var(--otk-theme-font-size-4xl, 28px)";
  }
  return "var(--otk-theme-font-size, 14px)";
}};

  font-weight: ${({ fontWeight }) => {
  if (fontWeight === textWeights.light) {
    return "var(--otk-theme-font-weight-light, 200)";
  }
  if (fontWeight === textWeights.regular) {
    return "var(--otk-theme-font-weight-regular, 400)";
  }
  if (fontWeight === textWeights.medium) {
    return "var(--otk-theme-font-weight-medium, 500)";
  }
  if (fontWeight === textWeights.bold) {
    return "var(--otk-theme-font-weight-bold, 700)";
  }
  if (fontWeight) {
    return fontWeight;
  }
  return "var(--otk-theme-font-weight, 400)";
}};

  line-height: ${({ lineHeight, size }) => {
  if (lineHeight) {
    return lineHeight;
  }
  if (size === textLineHeights.s) {
    return "var(--otk-theme-font-line-height-s, 12px)";
  }
  if (size === textLineHeights.xs) {
    return "var(--otk-theme-font-line-height-xs, 10px)";
  }
  if (size === textLineHeights.xxs) {
    return "var(--otk-theme-font-line-height-2xs, 8px)";
  }
  if (size === textLineHeights.m) {
    return "var(--otk-theme-font-line-height-m, 16px)";
  }
  if (size === textLineHeights.l) {
    return "var(--otk-theme-font-line-height-l, 18px)";
  }
  if (size === textLineHeights.xl) {
    return "var(--otk-theme-font-line-height-xl, 18px)";
  }
  if (size === textLineHeights.xxl) {
    return "var(--otk-theme-font-line-height-2xl, 20px)";
  }
  if (size === textLineHeights.xxxl) {
    return "var(--otk-theme-font-line-height-3xl, 24px)";
  }
  if (size === textLineHeights.xxxxl) {
    return "var(--otk-theme-font-line-height-4xl, 28px)";
  }
  return "var(--otk-theme-font-line-height-m, 16px)";
}};

  line-height: ${({ lineHeight }) => {
  if (lineHeight) {
    return lineHeight;
  }
}};

  text-transform: ${({ uppercase, lowercase, capitalize }) => {
  if (uppercase) {
    return "uppercase";
  }
  if (lowercase) {
    return "lowercase";
  }
  if (capitalize) {
    return "capitalize";
  }
  return "none";
}};

  display: ${({ display, truncate, truncateLines }) => {
  if (truncate) {
    return "block";
  }
  if (truncateLines) {
    return "-webkit-box";
  }
  if (display) {
    return display;
  }
  return "flex";
}};

  -webkit-box-orient: ${({ truncateLines }) => truncateLines ? "vertical" : "unset"};
  -webkit-line-clamp: ${({ truncateLines }) => truncateLines || "unset"};
  align-items: ${({ alignItems }) => alignItems || "center"};
  flex-direction: ${({ flexDirection }) => flexDirection || "row"};
  flex-grow: ${({ flexGrow }) => flexGrow || 0};
  flex-wrap: ${({ flexWrap }) => flexWrap || "wrap"};
  gap: ${({ gap }) => gap || "0"};
  justify-content: ${({ justifyContent }) => justifyContent || "start"};
  margin: ${({ margin }) => margin || "0"};
  max-height: ${({ maxHeight }) => maxHeight || "unset"};
  overflow: ${({ truncate, truncateLines }) => truncate || truncateLines ? "hidden" : "unset"};
  padding: ${({ padding }) => padding || "0"};
  text-align: ${({ textAlign }) => textAlign || "left"};
  text-overflow: ${({ truncate }) => truncate ? "ellipsis" : "unset"};
  white-space: ${({ truncate }) => truncate ? "nowrap" : "unset"};
  width: ${({ width }) => width || "auto"};
  word-break: ${({ wordBreak }) => wordBreak || "normal"};
`;

// src/components/text/texts-component.tsx
import styled12 from "styled-components";
var TextsComponent = styled12(FlexComponent)`
  width: 100%;
  flex-direction: column;
  align-items: ${({ alignItems }) => alignItems || "start"};
  justify-content: ${({ justifyContent }) => justifyContent || "start"};
  gap: ${({ gap }) => gap || "var(--otk-theme-space)"};
`;

// src/components/labeled-ball/labeled-ball-component.tsx
var LabelledBallWrapperComponent = styled13(BallComponent)`
  flex: none;
`;
function LabelledBallComponent({
  ballColor,
  label
}) {
  return /* @__PURE__ */ React6.createElement(FlexComponent, { alignItems: "center", gap: "var(--otk-theme-space-s)" }, /* @__PURE__ */ React6.createElement(LabelledBallWrapperComponent, { backgroundColor: ballColor }), /* @__PURE__ */ React6.createElement(TextComponent, { fontWeight: 500 }, label));
}

// src/components/modal/modal-component.tsx
import React7 from "react";
import styled15 from "styled-components";
import { CloseIcon } from "@ontruck/fe-icons";

// src/components/buttons/button-component.tsx
import styled14 from "styled-components";
var buttonSizes = {
  l: "l",
  m: "m",
  s: "s",
  xs: "xs",
  xxs: "2xs"
};
var buttonVariants = {
  ghost: "ghost",
  icon: "icon",
  text: "text",
  outline: "outline",
  soft: "soft"
};
var buttonColors = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "tertiary",
  danger: "danger",
  warning: "warning",
  success: "success",
  info: "info"
};
var ButtonComponent = styled14.button`
  border-radius: ${(props) => {
  if (props.circle && props.variant === buttonVariants.icon && (props.color === buttonColors.primary || props.color === buttonColors.secondary || props.color === buttonColors.tertiary || props.color === buttonColors.warning || props.color === buttonColors.success || props.color === buttonColors.info || props.color === buttonColors.danger)) {
    return "50%";
  }
  return "var(--otk-ui-button-border-radius, 4px);";
}};

  font-feature-settings:
    'clig' off,
    'liga' off;

  font-family: var(--otk-ui-button-font-family, sans-serif);
  font-style: normal;

  line-height: var(--otk-ui-button-line-height, 16px);

  cursor: ${(props) => {
  if (props.isDisabled) {
    return "not-allowed";
  }
  return "pointer";
}};

  font-size: ${(props) => {
  if (props.size === buttonSizes.m) {
    return "var(--otk-ui-button-m-font-size, 16px)";
  }
  if (props.size === buttonSizes.xs) {
    return "var(--otk-ui-button-xs-font-size, 12px)";
  }
  if (props.size === buttonSizes.xxs) {
    return "var(--otk-ui-button-xs-font-size, 12px)";
  }
  return "var(--otk-ui-button-font-size, 14px)";
}};

  font-weight: ${(props) => {
  if (props.variant === buttonVariants.text) {
    return "var(--otk-ui-button-font-weight, 200)";
  }
  return "var(--otk-ui-button-l-font-weight, 700)";
}};

  border: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    return "var(--otk-ui-button-icon-border, 1px solid transparent)";
  }
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-outline-disabled-border, 1px solid #bddff4)";
      }
      return "var(--otk-ui-button-primary-outline-border, 1px solid #39a0df)";
    }
    if (props.variant === buttonVariants.text) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-text-disabled-border, 1px solid transparent)";
      }
      return "var(--otk-ui-button-primary-text-border, , 1px solid transparent)";
    }
    if (props.variant === buttonVariants.ghost) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-ghost-disabled-border, 1px solid transparent)";
      }
      return "var(--otk-ui-button-primary-ghost-border, 1px solid transparent)";
    }
    if (props.variant === buttonVariants.soft) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-soft-disabled-border, 1px solid #bddff4)";
      }
      return "var(--otk-ui-button-primary-soft-border, #91caed)";
    }
    if (props.isDisabled) {
      return "var(--otk-ui-button-primary-disabled-border, 1px solid #bddff4)";
    }
    return "var(--otk-ui-button-primary-border, #39a0df)";
  }
  if (props.color === buttonColors.secondary) {
    if (props.isDisabled) {
      return "var(--otk-ui-button-secondary-disabled-border, 1px solid #bddff4)";
    }
    return "var(--otk-ui-button-secondary-border, #39a0df)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-border, 1px solid #94A6B8)";
  }
  if (props.isDisabled) {
    return "var(--otk-ui-button-disabled-border, 1px solid #e2e8ee)";
  }
  return "var(--otk-ui-button-border, 1px solid #94A6B8)";
}};

  background: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    if (props.color === buttonColors.primary) {
      return "var(--otk-ui-button-icon-primary-background, transparent)";
    }
    if (props.color === buttonColors.secondary) {
      return "var(--otk-ui-button-icon-secondary-background, transparent)";
    }
    return "var(--otk-ui-button-icon-background, transparent)";
  }
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-outline-disabled-background, transparent)";
      }
      return "var(--otk-ui-button-primary-outline-background, transparent)";
    }
    if (props.variant === buttonVariants.text) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-text-disabled-background, transparent)";
      }
      return "var(--otk-ui-button-primary-text-background, transparent)";
    }
    if (props.variant === buttonVariants.ghost) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-ghost-disabled-background, transparent)";
      }
      return "var(--otk-ui-button-primary-ghost-background, transparent)";
    }
    if (props.variant === buttonVariants.soft) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-soft-disabled-background, transparent)";
      }
      return "var(--otk-ui-button-primary-soft-background, #91caed)";
    }
    if (props.isDisabled) {
      return "var(--otk-ui-button-primary-disabled-background, transparent)";
    }
    return "var(--otk-ui-button-primary-background, #94A6B8)";
  }
  if (props.color === buttonColors.secondary) {
    if (props.isDisabled) {
      return "var(--otk-ui-button-secondary-disabled-background, transparent)";
    }
    return "var(--otk-ui-button-secondary-background, #94A6B8)";
  }
  if (props.variant === buttonVariants.outline) {
    return "var(--otk-ui-button-outline-background, transparent)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-background, transparent)";
  }
  if (props.isDisabled) {
    return "var(--otk-ui-button-disabled-background, #e2e8ee)";
  }
  return "var(--otk-ui-button-background, #94A6B8)";
}};

  color: ${(props) => {
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-outline-disabled-color, transparent)";
      }
      return "var(--otk-ui-button-primary-outline-color, #39a0df)";
    }
    if (props.variant === buttonVariants.text) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-text-disabled-color, transparent)";
      }
      return "var(--otk-ui-button-primary-text-color, #39a0df)";
    }
    if (props.variant === buttonVariants.ghost) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-ghost-disabled-color, transparent)";
      }
      return "var(--otk-ui-button-primary-ghost-color, #39a0df)";
    }
    if (props.variant === buttonVariants.soft) {
      if (props.isDisabled) {
        return "var(--otk-ui-button-primary-soft-disabled-color, transparent)";
      }
      return "var(--otk-ui-button-primary-soft-color, #19699a)";
    }
    if (props.isDisabled) {
      return "var(--otk-ui-button-primary-disabled-color, transparent)";
    }
    return "var(--otk-ui-button-primary-color, #fff)";
  }
  if (props.isDisabled) {
    if (props.variant === buttonVariants.outline) {
      return "var(--otk-ui-button-outline-disabled-color, #e2e8ee)";
    }
    if (props.variant === buttonVariants.ghost) {
      return "var(--otk-ui-button-ghost-disabled-color, #e2e8ee)";
    }
    return "var(--otk-ui-button-disabled-color, #fff)";
  }
  if (props.variant === buttonVariants.outline) {
    return "var(--otk-ui-button-outline-color, #94A6B8)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-color, #94A6B8)";
  }
  return "var(--otk-ui-button-color, #fff)";
}};

  width: ${(props) => {
  if (props.height) {
    return props.height;
  }
  if (props.variant === buttonVariants.icon && (props.color === buttonColors.primary || props.color === buttonColors.secondary || props.color === buttonColors.tertiary || props.color === buttonColors.warning || props.color === buttonColors.success || props.color === buttonColors.info || props.color === buttonColors.danger)) {
    if (props.size === buttonSizes.s) {
      return "var(--otk-ui-button-icon-s-size, 25px)";
    }
    return "var(--otk-ui-button-icon-size, 50px)";
  }
  if (props.variant === buttonVariants.icon && props.color !== buttonColors.primary && props.color !== buttonColors.secondary && props.color !== buttonColors.tertiary && props.color !== buttonColors.warning && props.color !== buttonColors.success && props.color !== buttonColors.info && props.color !== buttonColors.danger) {
    return "unset";
  }
  if (props.variant === buttonVariants.text) {
    return "auto";
  }
  if (props.fullWidth) {
    return "100%";
  }
  return "auto";
}};

  height: ${(props) => {
  if (props.height) {
    return props.height;
  }
  if (props.variant === buttonVariants.icon && (props.color === buttonColors.primary || props.color === buttonColors.secondary || props.color === buttonColors.tertiary || props.color === buttonColors.warning || props.color === buttonColors.success || props.color === buttonColors.info || props.color === buttonColors.danger)) {
    if (props.size === buttonSizes.xxs) {
      return "var(--otk-ui-button-icon-2xs-size, 15px)";
    }
    if (props.size === buttonSizes.s) {
      return "var(--otk-ui-button-icon-s-size, 25px)";
    }
    return "var(--otk-ui-button-icon-size, 50px)";
  }
  if (props.variant === buttonVariants.icon && props.color !== buttonColors.primary && props.color !== buttonColors.secondary && props.color !== buttonColors.tertiary && props.color !== buttonColors.warning && props.color !== buttonColors.success && props.color !== buttonColors.info && props.color !== buttonColors.danger) {
    return "unset";
  }
  return "auto";
}};

  flex: ${(props) => {
  if (props.flex) {
    return props.flex;
  }
  if (props.variant === buttonVariants.icon || props.variant === buttonVariants.text) {
    return "unset";
  }
  return "1 0 0";
}};

  display: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    if (props.color !== buttonColors.primary && props.color !== buttonColors.secondary && props.color !== buttonColors.tertiary && props.color !== buttonColors.warning && props.color !== buttonColors.success && props.color !== buttonColors.info && props.color !== buttonColors.danger) {
      return "block";
    }
    return "flex";
  }
  return "flex";
}};

  justify-content: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    if (props.color !== buttonColors.primary && props.color !== buttonColors.secondary && props.color !== buttonColors.tertiary && props.color !== buttonColors.warning && props.color !== buttonColors.success && props.color !== buttonColors.info && props.color !== buttonColors.danger) {
      return "unset";
    }
    return "center";
  }
  return "center";
}};

  align-items: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    if (props.color !== buttonColors.primary && props.color !== buttonColors.secondary && props.color !== buttonColors.tertiary && props.color !== buttonColors.warning && props.color !== buttonColors.success && props.color !== buttonColors.info && props.color !== buttonColors.danger) {
      return "unset";
    }
    return "center";
  }
  return "center";
}};

  gap: ${(props) => Boolean(props.variant) && props.variant === buttonVariants.icon ? "unset" : "8px"};

  padding: ${(props) => {
  if (props.variant === buttonVariants.icon) {
    return "unset";
  }
  if (props.size === buttonSizes.m) {
    return "var(--otk-ui-button-m-padding, 12px 20px)";
  }
  if (props.size === buttonSizes.s) {
    return "var(--otk-ui-button-s-padding, 6px 14px)";
  }
  if (props.size === buttonSizes.xs) {
    return "var(--otk-ui-button-xs-padding, 4px 8px)";
  }
  if (props.size === buttonSizes.xxs) {
    return "var(--otk-ui-button-2xs-padding, 2px 4px)";
  }
  return "var(--otk-ui-button-m-padding, 12px 20px)";
}};

  margin: ${(props) => {
  if (props.variant === buttonVariants.text) {
    return "var(--otk-ui-button-text-margin, 8px 16px)";
  }
  return "unset";
}};

  opacity: ${(props) => props.disabled ? "0.5" : "1"};
  pointer-events: ${(props) => props.disabled ? "none" : "auto"};

  :hover {
    border: ${(props) => {
  if (props.isDisabled) {
    return;
  }
  if (props.variant === buttonVariants.icon) {
    return "var(--otk-ui-button-icon-hover-border, 1px solid transparent)";
  }
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      return "var(--otk-ui-button-primary-outline-hover-border, 1px solid #39a0df)";
    }
    if (props.variant === buttonVariants.text) {
      return "var(--otk-ui-button-primary-text-hover-border, , 1px solid transparent)";
    }
    if (props.variant === buttonVariants.ghost) {
      return "var(--otk-ui-button-primary-ghost-hover-border, 1px solid transparent)";
    }
    if (props.variant === buttonVariants.soft) {
      return "var(--otk-ui-button-primary-soft-hover-border, #91caed)";
    }
    return "var(--otk-ui-button-primary-hover-border, #39a0df)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-hover-border, 1px solid #f2f5f8)";
  }
  return "var(--otk-ui-button-hover-border, 1px solid #94A6B8)";
}};

    color: ${(props) => {
  if (props.isDisabled) {
    return;
  }
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      return "var(--otk-ui-button-primary-outline-hover-color)";
    }
    if (props.variant === buttonVariants.text) {
      return "var(--otk-ui-button-primary-text-hover-color)";
    }
    if (props.variant === buttonVariants.ghost) {
      return "var(--otk-ui-button-primary-ghost-hover-color)";
    }
    if (props.variant === buttonVariants.soft) {
      return "var(--otk-ui-button-primary-soft-hover-color)";
    }
    return "var(--otk-ui-button-primary-hover-color, #fff)";
  }
  if (props.variant === buttonVariants.outline) {
    return "var(--otk-ui-button-outline-hover-color, #54708C)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-hover-color, #f2f5f8)";
  }
  return "var(--otk-ui-button-hover-color, #fff)";
}};

    background: ${(props) => {
  if (props.isDisabled) {
    return;
  }
  if (props.variant === buttonVariants.icon) {
    if (props.color === buttonColors.primary) {
      return "var(--otk-ui-button-icon-primary-hover-background, transparent)";
    }
    if (props.color === buttonColors.secondary) {
      return "var(--otk-ui-button-icon-secondary-hover-background, transparent)";
    }
    return "var(--otk-ui-button-icon-hover-background, 1px solid transparent)";
  }
  if (props.color === buttonColors.primary) {
    if (props.variant === buttonVariants.outline) {
      return "var(--otk-ui-button-primary-outline-hover-background)";
    }
    if (props.variant === buttonVariants.text) {
      return "var(--otk-ui-button-primary-text-hover-background)";
    }
    if (props.variant === buttonVariants.ghost) {
      return "var(--otk-ui-button-primary-ghost-hover-background)";
    }
    if (props.variant === buttonVariants.soft) {
      return "var(--otk-ui-button-primary-soft-hover-background)";
    }
    return "var(--otk-ui-button-primary-hover-background)";
  }
  if (props.variant === buttonVariants.outline) {
    return "var(--otk-ui-button-outline-hover-background)";
  }
  if (props.variant === buttonVariants.ghost) {
    return "var(--otk-ui-button-ghost-hover-background, #f2f5f8)";
  }
  return "var(--otk-ui-button-hover-background)";
}};
  }
`;

// src/components/modal/modal-component.tsx
var DEFAULT_CONFIRM_BUTTON_IS_LOADING_LABEL = "Confirmando";
var DEFAULT_CONFIRM_BUTTON_LABEL = "Confirmar";
var DEFAULT_CANCEL_BUTTON_LABEL = "Cancelar";
var ModalRootComponent = styled15.aside`
  display: flex;
  padding: var(--otk-ui-modal-container-padding, 24px);
  flex-direction: column;
  align-items: center;
  gap: var(--otk-ui-modal-container-padding, 24px);
  align-self: stretch;
  border-radius: var(--otk-ui-modal-container-border-radius, 8px);
  background: var(--otk-ui-modal-container-background, #f2f5f8);
  position: relative;
`;
var ModalSectionComponent = styled15.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--otk-ui-modal-section-gap, 16px);
  align-self: stretch;
`;
var ModalHeaderComponent = styled15.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--otk-ui-modal-header-gap, 4px);
  align-self: stretch;
`;
var ModalTitleComponent = styled15.div`
  color: var(--otk-ui-modal-title-color, #54708c);
  font-family: var(--otk-ui-modal-title-font-family, Roboto);
  font-size: var(--otk-ui-modal-title-font-size, 14px);
  font-style: normal;
  font-weight: var(--otk-ui-modal-title-font-weight, 500);
  line-height: normal;
`;
var ModalDescriptionComponent = styled15.div`
  color: var(--otk-ui-modal-description-color, #2396dc);
  font-family: var(--otk-ui-modal-description-font-family, Roboto);
  font-size: var(--otk-ui-modal-description-font-size, 18px);
  font-style: normal;
  font-weight: var(--otk-ui-modal-description-font-weight, 500);
  line-height: normal;
`;
var ModalFooterComponent = styled15.footer`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--otk-ui-modal-footer-gap, 8px);
  align-self: stretch;
`;
var CloseButtonContainer = styled15.div`
  position: absolute;
  right: var(--otk-ui-modal-close-button-right, 20px);
  top: var(--otk-ui-modal-close-button-top, 20px);
  width: var(--otk-ui-modal-close-button-size, 20px);
  height: var(--otk-ui-modal-close-button-size, 20px);
  cursor: pointer;
`;
function ModalComponent({
  bodyComponent,
  cancelButtonLabel,
  confirmButtonIsLoadingLabel,
  confirmButtonLabel,
  description,
  footerComponent,
  headerComponent,
  isCancelDisabled,
  isConfirmDisabled,
  isConfirmLoading,
  onCancel,
  onClose,
  onConfirm,
  title,
  isOpen = false
}) {
  if (!isOpen) {
    return null;
  }
  const renderHeaderComponent = () => {
    if (headerComponent) {
      return /* @__PURE__ */ React7.createElement(ModalHeaderComponent, null, headerComponent);
    }
    if (title && !description) {
      return /* @__PURE__ */ React7.createElement(ModalHeaderComponent, null, /* @__PURE__ */ React7.createElement(ModalTitleComponent, null, title));
    }
    if (!title && description) {
      return /* @__PURE__ */ React7.createElement(ModalHeaderComponent, null, /* @__PURE__ */ React7.createElement(ModalDescriptionComponent, null, description));
    }
    if (title && description) {
      return /* @__PURE__ */ React7.createElement(ModalHeaderComponent, null, /* @__PURE__ */ React7.createElement(ModalTitleComponent, null, title), /* @__PURE__ */ React7.createElement(ModalDescriptionComponent, null, description));
    }
    return null;
  };
  const renderFooterComponent = () => {
    if (footerComponent) {
      return /* @__PURE__ */ React7.createElement(ModalFooterComponent, null, footerComponent);
    }
    if (onCancel && onConfirm) {
      return /* @__PURE__ */ React7.createElement(ModalFooterComponent, null, /* @__PURE__ */ React7.createElement(
        ButtonComponent,
        {
          color: "primary",
          disabled: isCancelDisabled,
          onClick: onCancel,
          size: "s",
          variant: "outline"
        },
        cancelButtonLabel || DEFAULT_CANCEL_BUTTON_LABEL
      ), /* @__PURE__ */ React7.createElement(
        ButtonComponent,
        {
          color: "primary",
          disabled: isConfirmDisabled,
          onClick: () => {
            onConfirm();
          },
          size: "s"
        },
        isConfirmLoading ? confirmButtonIsLoadingLabel || DEFAULT_CONFIRM_BUTTON_IS_LOADING_LABEL : confirmButtonLabel || DEFAULT_CONFIRM_BUTTON_LABEL
      ));
    }
    if (onCancel && !onConfirm) {
      return /* @__PURE__ */ React7.createElement(ModalFooterComponent, null, /* @__PURE__ */ React7.createElement(
        ButtonComponent,
        {
          disabled: isCancelDisabled,
          onClick: onCancel,
          size: "s",
          variant: "outline"
        },
        cancelButtonLabel || DEFAULT_CANCEL_BUTTON_LABEL
      ));
    }
    if (!onCancel && onConfirm) {
      return /* @__PURE__ */ React7.createElement(ModalFooterComponent, null, /* @__PURE__ */ React7.createElement(
        ButtonComponent,
        {
          color: "primary",
          disabled: isConfirmDisabled,
          onClick: () => {
            onConfirm();
          },
          size: "s"
        },
        isConfirmLoading ? confirmButtonIsLoadingLabel || DEFAULT_CONFIRM_BUTTON_IS_LOADING_LABEL : confirmButtonLabel || DEFAULT_CONFIRM_BUTTON_LABEL
      ));
    }
    return null;
  };
  return /* @__PURE__ */ React7.createElement(ModalRootComponent, null, /* @__PURE__ */ React7.createElement(ModalSectionComponent, null, renderHeaderComponent(), bodyComponent ? bodyComponent : null), renderFooterComponent(), onClose ? /* @__PURE__ */ React7.createElement(CloseButtonContainer, { onClick: onClose }, /* @__PURE__ */ React7.createElement(CloseIcon, { color: "var(--otk-ui-modal-close-button-color)" })) : null);
}

// src/components/toolbar/toolbar-button-component.tsx
import styled16, { css } from "styled-components";
var ToolbarButtonComponent = styled16.div`
  display: flex;
  height: auto;
  cursor: pointer;
  display: flex;
  padding: 0px 16px;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  color: var(--otk-ui-toolbar-button-color);
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  height: ${({ height }) => height || "60px"};
  svg {
    path {
      fill: var(--otk-ui-toolbar-button-icon-color);
    }
  }
  ${(props) => Boolean(props.withBorder) && props.withBorder && css`
      border-left: 1px solid var(--accent-color-gray);
    `}
  ${(props) => Boolean(props.isDisabled) && props.isDisabled && css`
      cursor: default;
      color: var(--otk-ui-toolbar-button-is-disabled-color);
      svg {
        path {
          fill: var(--otk-ui-toolbar-button-icon-is-disabled-color);
        }
      }
    `}
`;

// src/components/toolbar/toolbar-component.tsx
import styled17 from "styled-components";
var ToolbarComponent = styled17.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 16px;
  padding: ${({ padding }) => padding || "16px"};
  background-color: transparent;
  height: 60px;
  border-bottom: 1px solid transparent;
`;

// src/components/toolbar/toolbar-content-component.tsx
import styled18 from "styled-components";
var ToolbarContentComponent = styled18.div`
  display: flex;
  flex-grow: 1;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;

// src/components/input/input.tsx
import React22 from "react";
import { SearchIcon as SearchIcon2 } from "@ontruck/fe-icons";

// src/components/input/types/text-input.tsx
import React9, { useRef as useRef4, useState as useState4 } from "react";

// src/components/input/parts/invisible-component.tsx
import styled19 from "styled-components";
var InvisibleComponent = styled19.div`
  opacity: 0;
  height: 0;
  width: 0;
  input {
    width: 1px;
  }
`;

// src/components/input/parts/premium-input-component.tsx
import styled20 from "styled-components";
var PremiumInputContainer = styled20.div`
  box-sizing: border-box;
  display: flex;

  ${({ padding }) => {
  if (padding) {
    return `padding: ${padding};`;
  }
  return `padding: var(--otk-ui-input-padding, 4px 8px 2px 8px);`;
}}

  justify-content: ${({ justifyContent }) => {
  if (justifyContent) {
    return `${justifyContent};`;
  }
  return "space-between;";
}};

  align-items: ${({ alignItems }) => {
  if (alignItems) {
    return `${alignItems};`;
  }
  return "center;";
}};

  flex-shrink: 0;
  cursor: text;
  position: relative;
  border-radius: var(--otk-ui-input-border-radius, 4px);

  input {
    background-color: transparent;
  }

  /* clears the ‘X’ from Internet Explorer */
  input[type='search']::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  input[type='search']::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  /* clears the ‘X’ from Chrome */
  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
    display: none;
  }

  background: ${(props) => {
  if (props.isDisabled) {
    return "var(--otk-ui-input-is-disabled-background, #E2E8EE)";
  }
  if (props.hasFocus) {
    return "var(--secondary-white, #fff)";
  }
  if (props.isReadOnly) {
    return "var(--otk-ui-input-is-readonly-background, #f2f5f8)";
  }
  if (props.hasError) {
    return "var(--otk-ui-input-has-error-background, #ffe0e2)";
  }
  return "var(--otk-ui-input-background, #fff)";
}};

  border: ${(props) => {
  if (props.border) {
    return props.border;
  }
  if (props.isDisabled || props.isReadOnly) {
    return "var(--otk-ui-input-is-disabled-border, 1px solid #ccd6e0)";
  }
  if (props.hasFocus) {
    return "var(--otk-ui-input-has-focus-border, 1px solid #0140cd)";
  }
  if (props.hasError) {
    return "var(--otk-ui-input-is-error-border, 1px solid #cc000a)";
  }
  return "var(--otk-ui-input-border, 1px solid #e2e8ee)";
}};

  width: ${(props) => {
  if (props.width) {
    return props.width;
  }
  return "100%";
}};

  height: ${(props) => {
  if (props.height) {
    return props.height;
  }
  return "var(--otk-ui-input-height, 40px)";
}};

  flex: ${(props) => {
  if (props.width) {
    return "none";
  }
  return "1 0 0";
}};

  cursor: ${(props) => {
  if (props.isDisabled) {
    return "not-allowed";
  }
}};

  ${({ overflow }) => {
  if (overflow) {
    return `overflow: ${overflow};`;
  }
  return "auto";
}}
`;

// src/components/input/parts/premium-label-big-component.tsx
import styled21 from "styled-components";
var PremiumLabelBigComponent = styled21.label`
  color: var(--grey-500, #94a6b8);
  font-feature-settings:
    'clig' off,
    'liga' off;
  /* 14px/regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
  cursor: text;
`;

// src/components/input/parts/premium-unit-component.tsx
import styled22 from "styled-components";
var PremiumUnitComponent = styled22.div`
  color: var(--grey700, #54708c);
  text-align: right;
  font-feature-settings:
    'clig' off,
    'liga' off;
  /* 12px/regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

// src/components/input/parts/label-and-value-component.tsx
import styled23 from "styled-components";
var LabelAndValueComponent = styled23.div`
  display: flex;
  flex-direction: column;

  justify-content: ${({ justifyContent }) => {
  if (justifyContent) {
    return `${justifyContent};`;
  }
  return `center;`;
}};

  align-items: flex-start;
  gap: 2px;
  width: 100%;
`;

// src/components/input/parts/premium-label-small-component.tsx
import styled24 from "styled-components";
var PremiumLabelSmallComponent = styled24.label`
  color: var(--grey-500, #94a6b8);
  font-feature-settings:
    'clig' off,
    'liga' off;
  /* 10px / regular - label */
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 8px; /* 80% */
  cursor: text;
`;

// src/components/input/parts/value-component.tsx
import styled25 from "styled-components";
var ValueComponent = styled25.div`
  width: 100%;
  input,
  select {
    width: 100%;
    outline: none;
    border: none;
    color: var(--grey700, #54708c);
    font-feature-settings:
      'clig' off,
      'liga' off;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 114.286% */
  }
  & {
    color: var(--grey700, #54708c);
    font-feature-settings:
      'clig' off,
      'liga' off;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 114.286% */
  }
`;

// src/components/input/parts/units-for-label-and-value-component.tsx
import styled26 from "styled-components";
var UnitsForLabelAndValueComponent = styled26.div`
  display: flex;
  padding-top: 12px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: ${(props) => Boolean(props.noPadding) && "0"};
  display: ${(props) => Boolean(props.noPadding) && "flex"};
  align-items: ${(props) => Boolean(props.noPadding) && "center"};
`;

// src/components/input/parts/require-indicator-component.tsx
import React8 from "react";
import styled27 from "styled-components";
var RequiredIndicatorComponent = styled27(
  ({ className }) => /* @__PURE__ */ React8.createElement("span", { className }, "*")
)`
  color: var(--error-900);
  margin-left: 5px;
`;

// src/components/input/types/text-input.tsx
function TextInput(_a) {
  var _b = _a, {
    label,
    type = "text",
    units,
    name,
    value,
    hasError = false,
    handleChangeEvent,
    width,
    isRequired = false,
    isDisabled = false
  } = _b, props = __objRest(_b, [
    "label",
    "type",
    "units",
    "name",
    "value",
    "hasError",
    "handleChangeEvent",
    "width",
    "isRequired",
    "isDisabled"
  ]);
  const id = name;
  const [hasFocus, setHasFocus] = useState4(false);
  const hasValue = Boolean(value) && value !== "";
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const inputRef = useRef4(null);
  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === "Tab") {
      setHasFocus(false);
    }
  };
  const triggerFocus = () => {
    if (isDisabled) {
      return;
    }
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  const removeFocus = () => {
    setHasFocus(false);
  };
  return /* @__PURE__ */ React9.createElement(
    PremiumInputContainer,
    {
      hasError,
      hasFocus,
      isDisabled,
      onClick: triggerFocus,
      ref,
      width
    },
    !showInput ? /* @__PURE__ */ React9.createElement(React9.Fragment, null, /* @__PURE__ */ React9.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React9.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React9.createElement(InvisibleComponent, null, /* @__PURE__ */ React9.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        id,
        onBlur: removeFocus,
        onChange: handleChangeEvent,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown,
        type,
        value
      }, props), {
        autoComplete: "off"
      })
    )), /* @__PURE__ */ React9.createElement(PremiumUnitComponent, null, units, " ")) : /* @__PURE__ */ React9.createElement(React9.Fragment, null, /* @__PURE__ */ React9.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React9.createElement(PremiumLabelSmallComponent, { htmlFor: name }, label, isRequired ? /* @__PURE__ */ React9.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React9.createElement(ValueComponent, null, /* @__PURE__ */ React9.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        id,
        name,
        onBlur: removeFocus,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown,
        ref: inputRef,
        type,
        value
      }, props), {
        autoComplete: "off",
        onChange: handleChangeEvent
      })
    ))), Boolean(units) && /* @__PURE__ */ React9.createElement(UnitsForLabelAndValueComponent, null, /* @__PURE__ */ React9.createElement(PremiumUnitComponent, null, units, " ")))
  );
}

// src/components/input/types/phone-input.tsx
import styled28 from "styled-components";
import React10, { useEffect as useEffect5, useState as useState5 } from "react";
import { parsePhoneNumber } from "react-phone-number-input";
import ReactPhoneInput from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
var PhoneInputContainer = styled28.div`
  input {
    width: 100%;
    outline: none;
    border: none;
    color: var(--grey700, #54708c);
    font-feature-settings:
      'clig' off,
      'liga' off;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 114.286% */
    ::placeholder {
      color: var(--grey-500, #94a6b8);
      opacity: 1; /* Firefox */
    }
    ::-ms-input-placeholder {
      /* Edge 12 -18 */
      color: var(--grey-500, #94a6b8);
    }
  }
  .PhoneInput {
    width: 100%;
  }
`;
var ValueComponentStyled = styled28(ValueComponent)`
  display: flex;
`;
function ContainerComponent({
  children,
  hasValue,
  label,
  hasError,
  isDisabled,
  isRequired,
  width,
  hasFocus,
  setHasFocus
}) {
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const triggerFocus = () => {
    setHasFocus(true);
    setTimeout(() => {
      var _a;
      (_a = ref.current) == null ? void 0 : _a.focus();
      if (ref.current) {
        const input = ref.current.querySelector("input");
        input == null ? void 0 : input.focus();
      }
    }, 1);
  };
  const showInput = hasFocus || hasValue;
  return /* @__PURE__ */ React10.createElement(
    PremiumInputContainer,
    {
      hasError,
      hasFocus,
      isDisabled,
      onClick: triggerFocus,
      ref,
      width
    },
    !showInput ? /* @__PURE__ */ React10.createElement(React10.Fragment, null, /* @__PURE__ */ React10.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React10.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React10.createElement(InvisibleComponent, null, /* @__PURE__ */ React10.createElement(
      "input",
      {
        autoComplete: "off",
        onBlur: () => {
          setHasFocus(false);
        },
        onFocus: triggerFocus,
        readOnly: true,
        type: "tel"
      }
    ))) : /* @__PURE__ */ React10.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React10.createElement(PremiumLabelSmallComponent, null, label, isRequired ? /* @__PURE__ */ React10.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React10.createElement(ValueComponentStyled, null, children))
  );
}
var DEFAULT_ALLOWED_COUNTRY_CODES = [
  "ES",
  // Spain
  "AD",
  // Andorra
  "AT",
  // Austria
  "BE",
  // Belgium
  "BG",
  // Bulgaria
  "CH",
  // Switzerland
  "CZ",
  // Czechia
  "DE",
  // Germany
  "DK",
  // Denmark
  "DZ",
  // Algeria
  "EE",
  // Estonia
  "FI",
  // Finland
  "FR",
  // France
  "GB",
  // UK
  "GR",
  // Greece
  "HR",
  // Croatia
  "HU",
  // Hungary
  "IE",
  // Ireland
  "IT",
  // Italy
  "LI",
  // Liechtenstein
  "LT",
  // Lithuania
  "LU",
  // Luxembourg
  "MA",
  // Morocco
  "ME",
  // Montenegro
  "MT",
  // Malta
  "NL",
  // Netherlands
  "NO",
  // Norway
  "PL",
  // Poland
  "PT",
  // Portugal
  "RO",
  // Romania
  "RS",
  // Serbia
  "SE",
  // Sweden
  "SI",
  // Slovenia
  "SK",
  // Slovakia
  "TN",
  // Tunisia
  "TR"
  // Turkey
];
var defaultPrefixForNumbersWithoutCountryPrefix = "+34";
function PhoneInput({
  label,
  name,
  value = "",
  hasError,
  handleChangeValue,
  isDisabled,
  width,
  isRequired,
  allowedCountries = DEFAULT_ALLOWED_COUNTRY_CODES
}) {
  const [hasFocus, setHasFocus] = useState5(false);
  let formatedValue = value;
  try {
    if (formatedValue.startsWith("0034")) {
      formatedValue = formatedValue.replace("0034", "+34");
    }
    if (formatedValue.length > 5 && !formatedValue.startsWith("+") && !formatedValue.startsWith("00")) {
      formatedValue = `${defaultPrefixForNumbersWithoutCountryPrefix}${formatedValue}`;
    }
  } catch (error) {
    formatedValue = value;
  }
  const parsedPhone = parsePhoneNumber(formatedValue);
  const phoneCountry = parsedPhone == null ? void 0 : parsedPhone.country;
  const shouldDisplayCountryPrefix = !phoneCountry || !allowedCountries.includes(phoneCountry);
  const translatedCountryLabels = en;
  const hasValue = Boolean(formatedValue) && formatedValue !== "";
  let allowedCountriesProps;
  if (typeof allowedCountries !== "undefined") {
    allowedCountriesProps = {
      countries: allowedCountries
    };
  } else {
    allowedCountriesProps = {};
  }
  const handleBlur = (event) => {
    event.preventDefault();
    const relatedTarget = event.relatedTarget;
    const isCountrySelect = relatedTarget == null ? void 0 : relatedTarget.className.includes("PhoneInputCountrySelect");
    const isCountryOption = relatedTarget == null ? void 0 : relatedTarget.className.includes("PhoneInputCountrySelectOption");
    if (!relatedTarget || !isCountrySelect && !isCountryOption) {
      setTimeout(() => {
        setHasFocus(false);
      }, 100);
    }
  };
  useEffect5(() => {
    const handleClick = (e) => {
      const target = e.target;
      if (target.closest(".PhoneInputCountrySelect")) {
        e.stopPropagation();
      }
    };
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);
  return /* @__PURE__ */ React10.createElement(
    ReactPhoneInput,
    __spreadProps(__spreadValues({
      autoComplete: "off",
      defaultCountry: "ES"
    }, allowedCountriesProps), {
      containerComponent: ContainerComponent,
      containerComponentProps: {
        hasValue,
        label,
        hasError,
        isDisabled,
        width,
        isRequired,
        hasFocus,
        setHasFocus
      },
      countrySelectProps: {
        onClick: () => {
          setHasFocus(true);
        },
        className: "PhoneInputCountrySelect"
      },
      disabled: isDisabled,
      id: name,
      initialValueFormat: shouldDisplayCountryPrefix ? void 0 : "national",
      labels: translatedCountryLabels,
      name,
      onBlur: handleBlur,
      onChange: (newValue) => {
        if (handleChangeValue) {
          if (newValue)
            handleChangeValue(name, newValue.toString());
          else {
            handleChangeValue(name, "");
          }
        }
      },
      value: formatedValue
    })
  );
}

// src/components/input/types/rating-input.tsx
import React11 from "react";
import styled29, { css as css2 } from "styled-components";
import { StarIcon } from "@ontruck/fe-icons";
var CustomPremiumInputContainer = styled29(PremiumInputContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--grey-200, #e2e8ee);
`;
var StarContainer = styled29.div`
  input {
    display: none;
  }
  cursor: pointer;
  svg {
    padding: 8px;
    width: 20px;
    height: 20px;
    cursor: pointer;
    box-sizing: content-box;
  }
  svg path {
    fill: white;
  }
  ${(props) => Boolean(props.isFilled) && css2`
      svg path {
        fill: var(--login-button-bg, red);
      }
    `}
`;
function RatingInput({ name, value, handleChangeEvent }) {
  const options = [1, 2, 3, 4, 5];
  let numberValue = 0;
  if (value !== void 0) {
    try {
      numberValue = parseInt(value);
      if (isNaN(numberValue))
        numberValue = 0;
    } catch (e) {
      numberValue = 0;
    }
  }
  return /* @__PURE__ */ React11.createElement(CustomPremiumInputContainer, null, options.map((option) => {
    return /* @__PURE__ */ React11.createElement(StarContainer, { isFilled: numberValue >= option, key: option }, /* @__PURE__ */ React11.createElement("label", { htmlFor: `rating-input-${name}-${option}` }, /* @__PURE__ */ React11.createElement(
      "input",
      {
        id: `rating-input-${name}-${option}`,
        name,
        onChange: handleChangeEvent,
        type: "radio",
        value: option
      }
    ), /* @__PURE__ */ React11.createElement(StarIcon, null)));
  }));
}

// src/components/input/select/select-input-component.tsx
import React12, { useState as useState6, useRef as useRef5, useLayoutEffect, useEffect as useEffect6 } from "react";
import { createPortal } from "react-dom";

// src/components/dropdown/dropdown-container.tsx
import styled30 from "styled-components";
var DropdownContainer = styled30.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 40px;
  max-height: 185px;
  border: 1px solid var(--otk-theme-color-grey-500);
  border-radius: 4px;
  background-color: white;
  display: flex;
  flex-direction: column;
  overflow: ${({ overflow }) => overflow || "auto"};
  z-index: 9999;
  max-height: ${(props) => Boolean(props.maxHeight) && props.maxHeight};
  ${(props) => props.growTop && "top: -400%;"};
`;

// src/components/dropdown/dropdown-item-container.tsx
import styled31 from "styled-components";
var DropdownItemContainer = styled31.div`
  padding: 10px;
  width: 100%;
  outline: none;
  border: none;
  color: var(--grey700);
  font-feature-settings:
    'clig' off,
    'liga' off;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
  border-bottom: 1px solid var(--grey300);
  cursor: pointer;
  font-weight: ${(props) => Boolean(props.isSelected) && "bolder"};
  cursor: ${(props) => Boolean(props.hasPlaceholder) && "default"};
  background-color: ${(props) => Boolean(props.hasPlaceholder) && "var(--grey300)"};

  &:hover {
    background-color: var(--grey200);
  }
`;

// src/components/dropdown/dropdown-portal-component.tsx
import styled32 from "styled-components";
var DropdownPortalComponent = styled32.div`
  position: absolute;
  top: ${({ top, growTop }) => growTop ? top - 400 : top}px;
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  max-height: 185px;
  border: 1px solid var(--otk-theme-color-grey-500);
  border-radius: 4px;
  background-color: white;
  display: flex;
  flex-direction: column;
  overflow: ${({ overflow }) => overflow || "auto"};
  z-index: 99999;
`;

// src/components/input/select/select-input-component.tsx
function SelectInputComponent({
  className,
  growTop = false,
  handleChangeValue,
  hasError = false,
  isDisabled = false,
  isRequired = false,
  label,
  name = "",
  options = [],
  portal = false,
  value,
  width
}) {
  const [hasFocus, setHasFocus] = useState6(false);
  const [dropdownPosition, setDropdownPosition] = useState6({ top: 0, left: 0, width: 0 });
  const portalDropdownRef = useRef5(null);
  const inputRef = useRef5(null);
  useEffect6(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isOutsideInput = inputRef.current && !inputRef.current.contains(target);
      const isOutsidePortal = !portal || portalDropdownRef.current && !portalDropdownRef.current.contains(target);
      if (isOutsideInput && isOutsidePortal) {
        setHasFocus(false);
      }
    };
    if (hasFocus) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasFocus, portal]);
  useLayoutEffect(() => {
    if (hasFocus && portal && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [hasFocus, portal]);
  const handleOnChange = (valueSelected) => {
    if (isDisabled) {
      return;
    }
    if (handleChangeValue) {
      handleChangeValue(name, valueSelected);
    }
    setHasFocus(false);
  };
  let labelComponent = null;
  if (label) {
    const LabelComponent = value ? PremiumLabelSmallComponent : PremiumLabelBigComponent;
    labelComponent = /* @__PURE__ */ React12.createElement(LabelComponent, null, label, isRequired ? /* @__PURE__ */ React12.createElement(RequiredIndicatorComponent, null) : null);
  }
  const selectedOption = options.find((option) => option.value === value) || options[0];
  const hasValue = value !== void 0;
  const renderDropdownOptions = () => {
    if (options.length === 0) {
      return null;
    }
    return /* @__PURE__ */ React12.createElement(React12.Fragment, null, options.map((item) => /* @__PURE__ */ React12.createElement(
      DropdownItemContainer,
      {
        isSelected: item.value === value,
        key: item.value,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOnChange(item.value);
        }
      },
      item.component ? item.component : item.label
    )));
  };
  return /* @__PURE__ */ React12.createElement(React12.Fragment, null, /* @__PURE__ */ React12.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      onClick: () => {
        if (isDisabled) {
          return;
        }
        setHasFocus(true);
      },
      ref: inputRef,
      width
    },
    hasFocus && !portal ? /* @__PURE__ */ React12.createElement(DropdownContainer, { growTop }, renderDropdownOptions()) : null,
    /* @__PURE__ */ React12.createElement(LabelAndValueComponent, null, labelComponent, hasValue ? /* @__PURE__ */ React12.createElement(ValueComponent, null, selectedOption.label, /* @__PURE__ */ React12.createElement(InvisibleComponent, null, /* @__PURE__ */ React12.createElement(
      "input",
      {
        onBlur: () => {
          setHasFocus(false);
        },
        onFocus: () => {
          setHasFocus(true);
        }
      }
    ))) : null),
    /* @__PURE__ */ React12.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React12.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React12.createElement(
      "svg",
      {
        fill: "none",
        height: "16",
        viewBox: "0 0 16 16",
        width: "16",
        xmlns: "http://www.w3.org/2000/svg"
      },
      /* @__PURE__ */ React12.createElement(
        "path",
        {
          d: "M10.9342 6.20291C10.8435 6.07757 10.6731 6.00002 10.4884 6H5.51147C5.32689 6.00007 5.15666 6.07757 5.06599 6.20282C4.97532 6.32807 4.97827 6.48165 5.0737 6.60473L7.56218 9.80778C7.65489 9.9271 7.82091 10 7.99996 10C8.179 10 8.34502 9.9271 8.43773 9.80778L10.9262 6.60473C11.0217 6.4817 11.0247 6.32816 10.9342 6.20291Z",
          fill: "#54708C"
        }
      )
    )))
  ), hasFocus && portal ? createPortal(
    /* @__PURE__ */ React12.createElement(
      DropdownPortalComponent,
      {
        growTop,
        left: dropdownPosition.left,
        ref: portalDropdownRef,
        top: dropdownPosition.top,
        width: dropdownPosition.width
      },
      renderDropdownOptions()
    ),
    document.body
  ) : null);
}

// src/components/input/select/select-with-internal-search-input-component.tsx
import React14, {
  useState as useState8,
  useMemo,
  useRef as useRef6,
  useLayoutEffect as useLayoutEffect2,
  useEffect as useEffect7
} from "react";
import { createPortal as createPortal2 } from "react-dom";
import styled34 from "styled-components";
import { CloseIcon as CloseIcon2, SearchIcon } from "@ontruck/fe-icons";

// src/components/input/types/text-input-with-icon.tsx
import React13, { useState as useState7 } from "react";
import styled33 from "styled-components";
var IconContainer = styled33.div`
  path {
    fill: var(--grey300, #979797);
  }
`;
function TextInputWithIconComponent(_a) {
  var _b = _a, {
    autoFocus = false,
    className,
    hasError = false,
    id,
    isDisabled = false,
    isRequired = false,
    label,
    name = "",
    handleChangeEvent,
    value,
    width,
    icon
  } = _b, props = __objRest(_b, [
    "autoFocus",
    "className",
    "hasError",
    "id",
    "isDisabled",
    "isRequired",
    "label",
    "name",
    "handleChangeEvent",
    "value",
    "width",
    "icon"
  ]);
  const [hasFocus, setHasFocus] = useState7(false);
  const clickRefFrom = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const [inputRef, setInputFocus] = useFocusHook();
  const hasValue = value !== void 0 && value !== "";
  const showInput = hasFocus || hasValue;
  return /* @__PURE__ */ React13.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      onClick: () => {
        if (isDisabled) {
          return;
        }
        setHasFocus(true);
        setInputFocus();
      },
      ref: clickRefFrom,
      width
    },
    !showInput ? /* @__PURE__ */ React13.createElement(React13.Fragment, null, /* @__PURE__ */ React13.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React13.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React13.createElement(InvisibleComponent, null, /* @__PURE__ */ React13.createElement(
      "input",
      {
        autoFocus,
        disabled: isDisabled,
        id,
        name,
        onBlur: () => {
          setHasFocus(false);
        },
        onFocus: () => {
          setHasFocus(true);
          setInputFocus();
        },
        readOnly: true,
        ref: inputRef,
        type: "search",
        value
      }
    )), /* @__PURE__ */ React13.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React13.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React13.createElement(IconContainer, null, icon !== void 0 ? icon : null)))) : /* @__PURE__ */ React13.createElement(React13.Fragment, null, /* @__PURE__ */ React13.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React13.createElement(PremiumLabelSmallComponent, { htmlFor: id }, label, isRequired ? /* @__PURE__ */ React13.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React13.createElement(ValueComponent, null, /* @__PURE__ */ React13.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        id,
        ref: inputRef,
        type: "search",
        value
      }, props), {
        onBlur: () => {
          setHasFocus(false);
        },
        onChange: handleChangeEvent,
        onFocus: () => {
          setHasFocus(true);
          setInputFocus();
        },
        onKeyDown: (event) => {
          if (props.onKeyDown) {
            props.onKeyDown(event);
          } else if (event.key === "Enter") {
            if (handleChangeEvent) {
              handleChangeEvent(event);
            }
          }
        }
      })
    ))), /* @__PURE__ */ React13.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React13.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React13.createElement(IconContainer, null, icon !== void 0 ? icon : null))))
  );
}

// src/components/input/select/select-with-internal-search-input-component.tsx
var NoOptionsComponent = styled34.div`
  padding: 24px;
`;
var SearchInputContainer = styled34.div`
  background: var(--input-background);
  padding: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
`;
var OptionsContainer = styled34.div`
  overflow-y: auto;
  max-height: 180px;
`;
var ClearButton = styled34.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 50%;
  height: 20px;
  width: 20px;
  background-color: var(--grey300);
  color: var(--grey700);
  gap: 8px;
  cursor: pointer;
  border: none;
  svg {
    width: 10px;
    height: 10px;
    path {
      fill: var(--white);
    }
  }
`;
var normalizeTextForSearch = (text) => {
  return text.toUpperCase().normalize("NFD").replace(new RegExp("\\p{Diacritic}", "gu"), "").replace(/-| /g, "");
};
var search = (source, text) => {
  return normalizeTextForSearch(source).includes(normalizeTextForSearch(text));
};
var getFilteredOptions = (options, filterString) => {
  if (filterString.trim() === "")
    return options;
  return options.filter((option) => {
    var _a;
    return search((_a = option.searchValue) != null ? _a : option.label, filterString);
  });
};
function SelectWithInternalSearchInputComponent({
  options = [],
  className,
  hasError = false,
  isDisabled = false,
  handleChangeValue,
  width,
  value,
  name = "",
  label,
  isRequired = false,
  noOptionsText,
  isClearable = false,
  portal = false
}) {
  const [hasFocus, setHasFocus] = useState8(false);
  const [searchValue, setSearchValue] = useState8("");
  const [dropdownPosition, setDropdownPosition] = useState8({ top: 0, left: 0, width: 0 });
  const portalDropdownRef = useRef6(null);
  const clickRefFrom = useOutsideClickHook(() => {
    if (portal)
      return;
    setHasFocus(false);
  });
  const inputRef = clickRefFrom;
  useEffect7(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isOutsideInput = inputRef.current && !inputRef.current.contains(target);
      const isOutsidePortal = !portal || portalDropdownRef.current && !portalDropdownRef.current.contains(target);
      if (isOutsideInput && isOutsidePortal) {
        setHasFocus(false);
      }
    };
    if (hasFocus) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hasFocus, portal]);
  useLayoutEffect2(() => {
    if (hasFocus && portal && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [hasFocus, portal]);
  const handleOnChange = (valueSelected) => {
    if (isDisabled) {
      return;
    }
    if (handleChangeValue) {
      handleChangeValue(name, valueSelected);
    }
    setHasFocus(false);
  };
  let labelComponent = null;
  if (label) {
    const LabelComponent = value ? PremiumLabelSmallComponent : PremiumLabelBigComponent;
    labelComponent = /* @__PURE__ */ React14.createElement(LabelComponent, null, label, isRequired ? /* @__PURE__ */ React14.createElement(RequiredIndicatorComponent, null) : null);
  }
  const selectedOption = useMemo(() => {
    if (options.length > 0) {
      return options.find((option) => option.value === value) || options[0];
    }
    if (value) {
      return {
        label: value,
        value,
        searchValue: value
      };
    }
    return void 0;
  }, [options, value]);
  const hasValue = value !== void 0;
  const filteredOptions = options.length > 0 ? getFilteredOptions(options, searchValue) : [];
  const dropdownContent = /* @__PURE__ */ React14.createElement("div", null, /* @__PURE__ */ React14.createElement(SearchInputContainer, null, /* @__PURE__ */ React14.createElement(
    TextInputWithIconComponent,
    {
      autoFocus: true,
      handleChangeEvent: (e) => {
        const newSearchValue = e.currentTarget.value;
        setSearchValue(newSearchValue);
      },
      icon: /* @__PURE__ */ React14.createElement(SearchIcon, null),
      label: "Buscar",
      name: "search",
      value: searchValue
    }
  ), isClearable ? /* @__PURE__ */ React14.createElement(
    ClearButton,
    {
      onClick: () => {
        setSearchValue("");
        handleOnChange(void 0);
      },
      type: "button"
    },
    /* @__PURE__ */ React14.createElement(CloseIcon2, null)
  ) : null), /* @__PURE__ */ React14.createElement(OptionsContainer, null, filteredOptions.length > 0 ? filteredOptions.map((item) => /* @__PURE__ */ React14.createElement(
    DropdownItemContainer,
    {
      isSelected: item.value === value,
      key: item.value,
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleOnChange(item.value);
      }
    },
    item.color ? /* @__PURE__ */ React14.createElement(BallComponent, { backgroundColor: item.color }) : null,
    item.label
  )) : /* @__PURE__ */ React14.createElement(NoOptionsComponent, null, noOptionsText || "No se encontraron opciones")));
  return /* @__PURE__ */ React14.createElement(React14.Fragment, null, /* @__PURE__ */ React14.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      onClick: () => {
        if (isDisabled) {
          return;
        }
        setHasFocus(true);
      },
      ref: clickRefFrom,
      width
    },
    hasFocus && !portal ? /* @__PURE__ */ React14.createElement(DropdownContainer, { maxHeight: "240px", overflow: "hidden" }, dropdownContent) : null,
    /* @__PURE__ */ React14.createElement(LabelAndValueComponent, null, labelComponent, hasValue ? /* @__PURE__ */ React14.createElement(ValueComponent, null, (selectedOption == null ? void 0 : selectedOption.label) ? selectedOption.label : null, /* @__PURE__ */ React14.createElement(InvisibleComponent, null, /* @__PURE__ */ React14.createElement(
      "input",
      {
        onBlur: () => {
          setHasFocus(false);
        },
        onFocus: () => {
          setHasFocus(true);
        }
      }
    ))) : null),
    /* @__PURE__ */ React14.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React14.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React14.createElement(
      "svg",
      {
        fill: "none",
        height: "16",
        viewBox: "0 0 16 16",
        width: "16",
        xmlns: "http://www.w3.org/2000/svg"
      },
      /* @__PURE__ */ React14.createElement(
        "path",
        {
          d: "M10.9342 6.20291C10.8435 6.07757 10.6731 6.00002 10.4884 6H5.51147C5.32689 6.00007 5.15666 6.07757 5.06599 6.20282C4.97532 6.32807 4.97827 6.48165 5.0737 6.60473L7.56218 9.80778C7.65489 9.9271 7.82091 10 7.99996 10C8.179 10 8.34502 9.9271 8.43773 9.80778L10.9262 6.60473C11.0217 6.4817 11.0247 6.32816 10.9342 6.20291Z",
          fill: "#54708C"
        }
      )
    )))
  ), hasFocus && portal ? createPortal2(
    /* @__PURE__ */ React14.createElement(
      DropdownPortalComponent,
      {
        growTop: false,
        left: dropdownPosition.left,
        overflow: "hidden",
        ref: portalDropdownRef,
        top: dropdownPosition.top,
        width: dropdownPosition.width
      },
      dropdownContent
    ),
    document.body
  ) : null);
}

// src/components/input/types/date-input.tsx
import React15, { useRef as useRef7, useState as useState9 } from "react";
import styled35 from "styled-components";
import { CalendarIcon, CloseIcon as CloseIcon3 } from "@ontruck/fe-icons";
import ReactDatePicker from "react-datepicker";
var IconsContainer = styled35.div`
  display: flex;
  gap: var(--otk-theme-space-s);
  align-items: center;
`;
var StyledClearButton = styled35(ClearButton)`
  width: var(--otk-theme-space-m);
  height: var(--otk-theme-space-m);
`;
var DatepickerContainer = styled35.div`
  ${({ showOnlyDatePicker }) => {
  if (!showOnlyDatePicker) {
    return `
        z-index: 300;
        top: 40px;
        left: 0px;
        position: absolute;
      `;
  }
}}

  .react-datepicker {
    width: 100%;
    border: var(--otk-ui-datepicker-border, 1px solid #aeaeae);

    .react-datepicker__navigation {
      &.react-datepicker__navigation--next {
        border-left-color: var(--otk-ui-datepicker-navigation-next-border-left-color, #ccc);

        &:hover {
          border-left-color: var(
            --otk-ui-datepicker-navigation-next-hover-border-left-color,
            rgb(178.5, 178.5, 178.5)
          );
        }
      }

      &.react-datepicker__navigation--previous {
        border-right-color: var(--otk-ui-datepicker-navigation-next-border-left-color, #ccc);

        &:hover {
          border-right-color: var(
            --otk-ui-datepicker-navigation-next-hover-border-left-color,
            rgb(178.5, 178.5, 178.5)
          );
        }
      }
    }

    .react-datepicker__header {
      background-color: var(--otk-ui-datepicker-header-background-color, #f0f0f0);
      border-bottom: var(--otk-ui-datepicker-header-border-bottom, 1px solid #aeaeae);
      border-top-left-radius: var(--otk-ui-datepicker-header-border-top-left-radius, 0.3rem);
      border-top-right-radius: var(--otk-ui-datepicker-header-border-top-right-radius, 0.3rem);
      padding-top: var(--otk-ui-datepicker-header-padding-top, 8px);
      position: var(--otk-ui-datepicker-header-position, relative);

      .react-datepicker__current-month {
        margin-top: 5px;
        text-transform: var(--otk-ui-datepicker-day-name-text-transform, none);
        color: var(--otk-ui-datepicker-current-month-color, #000);
      }

      .react-datepicker__day-names {
        margin-top: var(--otk-ui-datepicker-day-names-margin-top, 0);

        .react-datepicker__day-name {
          color: var(--otk-ui-datepicker-day-name-color, #000);
          text-transform: var(--otk-ui-datepicker-day-name-text-transform, none);
        }
      }
    }

    .react-datepicker__month {
      .react-datepicker__week {
        .react-datepicker__day {
          color: var(--otk-ui-datepicker-day-color, #000);

          :hover {
            color: var(--otk-ui-datepicker-day-hover-color, #000);
            border-radius: var(--otk-ui-datepicker-day-border-radius, 0.3em);
          }
        }

        .react-datepicker__day--highlighted {
          background-color: var(--otk-ui-datepicker-day-highlighted-background-color, #216ba5);
          color: var(--otk-ui-datepicker-day-highlighted-color, #000);

          &:hover {
            color: var(--otk-ui-datepicker-day-highlighted-hover-color, #000);
          }
        }
        .react-datepicker__day--selected {
          border-radius: var(--otk-ui-datepicker-day-selected-border-radius, 0.3em);
          background-color: var(--otk-ui-datepicker-day-selected-background-color, #216ba5);
          color: var(--otk-ui-datepicker-day-selected-color, #000);
        }
      }
    }
  }

  .react-datepicker__time-container {
    width: 100%;
  }

  .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box {
    width: 100%;
  }
`;
function DateInput(_a) {
  var _b = _a, {
    label,
    units,
    name,
    value,
    hasError,
    handleChangeValue,
    onChangeDate,
    isRequired,
    width,
    disableClear,
    isDisabled,
    showOnlyDatePicker = false,
    shouldCloseOnSelect = true,
    datePickerClassName,
    highlightDatepickerDates,
    selectedDatepickerDate,
    includedDatepickerDates
  } = _b, props = __objRest(_b, [
    "label",
    "units",
    "name",
    "value",
    "hasError",
    "handleChangeValue",
    "onChangeDate",
    "isRequired",
    "width",
    "disableClear",
    "isDisabled",
    "showOnlyDatePicker",
    "shouldCloseOnSelect",
    "datePickerClassName",
    "highlightDatepickerDates",
    "selectedDatepickerDate",
    "includedDatepickerDates"
  ]);
  const id = name;
  const [hasFocus, setHasFocus] = useState9(false);
  const hasValue = Boolean(value) && value !== "";
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const inputRef = useRef7(null);
  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === "Tab") {
      setHasFocus(false);
    }
  };
  const triggerFocus = () => {
    if (isDisabled)
      return;
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  const removeFocus = () => {
    setHasFocus(false);
  };
  const onChange = (date, event) => {
    if (!event || !date) {
      return;
    }
    event.stopPropagation();
    const dateStr = date.toISOString();
    if (onChangeDate) {
      onChangeDate(date, event);
    }
    if (handleChangeValue) {
      handleChangeValue(name, dateStr);
    }
    setHasFocus(false);
  };
  const clearValue = (event) => {
    event.stopPropagation();
    if (isDisabled)
      return;
    if (handleChangeValue) {
      handleChangeValue(name, void 0);
    }
    setHasFocus(false);
  };
  const includedDatesProps = {};
  if (includedDatepickerDates) {
    includedDatesProps.includeDates = includedDatepickerDates;
  }
  return /* @__PURE__ */ React15.createElement(
    PremiumInputContainer,
    {
      border: showOnlyDatePicker ? "none" : void 0,
      hasError,
      hasFocus,
      isDisabled,
      justifyContent: showOnlyDatePicker ? "center" : "space-between",
      onClick: triggerFocus,
      ref,
      width
    },
    /* @__PURE__ */ React15.createElement(React15.Fragment, null, hasFocus && !isDisabled || showOnlyDatePicker ? /* @__PURE__ */ React15.createElement(DatepickerContainer, { showOnlyDatePicker }, /* @__PURE__ */ React15.createElement(
      ReactDatePicker,
      __spreadProps(__spreadValues({
        calendarClassName: datePickerClassName,
        highlightDates: highlightDatepickerDates
      }, includedDatesProps), {
        inline: true,
        onChange,
        selected: selectedDatepickerDate,
        shouldCloseOnSelect
      })
    )) : null, !showOnlyDatePicker && (showInput ? /* @__PURE__ */ React15.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React15.createElement(PremiumLabelSmallComponent, { htmlFor: name }, label, isRequired ? /* @__PURE__ */ React15.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React15.createElement(ValueComponent, null, /* @__PURE__ */ React15.createElement(
      "input",
      __spreadProps(__spreadValues({
        id,
        name,
        onChange: removeFocus,
        onKeyDown: handleKeyDown,
        ref: inputRef,
        type: "text",
        value: value || ""
      }, props), {
        autoComplete: "off",
        disabled: isDisabled
      })
    ))) : /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React15.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React15.createElement(InvisibleComponent, null, /* @__PURE__ */ React15.createElement("input", { onBlur: removeFocus, onFocus: triggerFocus })), /* @__PURE__ */ React15.createElement(PremiumUnitComponent, null, units, " "))), !showOnlyDatePicker ? /* @__PURE__ */ React15.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React15.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React15.createElement(IconsContainer, null, hasValue && !disableClear && !isDisabled ? /* @__PURE__ */ React15.createElement(StyledClearButton, { onClick: clearValue, type: "button" }, /* @__PURE__ */ React15.createElement(CloseIcon3, null)) : null, /* @__PURE__ */ React15.createElement(CalendarIcon, { color: "var(--otk-theme-color-grey-500)" })))) : null)
  );
}

// src/components/input/types/time-input-component.tsx
import React16, { useRef as useRef8, useState as useState10 } from "react";
var getProcessedValueForTimeInput = (value) => {
  try {
    const first5Chars = value.substring(0, 5);
    const withNoWeirdChars = first5Chars.replace(/[^\d:-]/g, "");
    const processedValue = withNoWeirdChars;
    const length = processedValue.length;
    const pos0Value = parseInt(value[0]);
    const isPos0Valid = !isNaN(pos0Value) && pos0Value >= 0 && pos0Value <= 9;
    if (length === 1)
      return isPos0Valid ? processedValue : "";
    if (!isPos0Valid)
      return "";
    const pos1Value = value[1];
    let isPos1Valid = pos1Value === ":";
    if (!isPos1Valid) {
      const pos1Num = parseInt(pos1Value);
      if (pos0Value <= 1) {
        isPos1Valid = !isNaN(pos1Num) && pos1Num >= 0 && pos1Num <= 9;
      } else if (pos0Value === 2) {
        isPos1Valid = !isNaN(pos1Num) && pos1Num >= 0 && pos1Num <= 3;
      }
    }
    if (length === 2) {
      if (isPos1Valid && pos1Value !== ":") {
        return `${processedValue}:`;
      }
      return isPos1Valid ? processedValue : processedValue.substring(0, 1);
    }
    if (length === 3) {
      const pos2Value = parseInt(value[2]);
      const isPos2Valid = !isNaN(pos2Value) && pos2Value >= 0 && pos2Value <= 5;
      return isPos2Valid ? processedValue : processedValue.substring(0, 2);
    }
    if (length === 4) {
      const pos3Value = parseInt(value[3]);
      const isPos3Valid = !isNaN(pos3Value) && pos3Value >= 0 && pos3Value <= 9;
      return isPos3Valid ? processedValue : processedValue.substring(0, 3);
    }
    if (length === 5) {
      return processedValue;
    }
    return processedValue;
  } catch (e) {
    return "";
  }
};
function TimeInputComponent(_a) {
  var _b = _a, {
    className,
    handleChangeValue,
    hasError,
    isDisabled,
    isRequired,
    label,
    name,
    value = "",
    width,
    units
  } = _b, props = __objRest(_b, [
    "className",
    "handleChangeValue",
    "hasError",
    "isDisabled",
    "isRequired",
    "label",
    "name",
    "value",
    "width",
    "units"
  ]);
  const [hasFocus, setHasFocus] = useState10(false);
  const inputRef = useRef8(null);
  const hasValue = Boolean(value) && value !== "";
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const triggerFocus = () => {
    if (isDisabled)
      return;
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  const removeFocus = () => {
    setHasFocus(false);
  };
  const handleChange = (event) => {
    const processedValue = getProcessedValueForTimeInput(event.target.value);
    if (handleChangeValue) {
      handleChangeValue(name, processedValue);
    }
  };
  const handleKeyDown = (event) => {
    if (event.shiftKey && event.key === "Tab") {
      setHasFocus(false);
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      const target = event.currentTarget;
      const { selectionStart, selectionEnd, value: currentValue } = target;
      if (selectionStart === null || selectionEnd === null)
        return;
      if (selectionStart === 0 && selectionStart === selectionEnd)
        return;
      const newValue = selectionStart !== selectionEnd ? currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd) : currentValue.slice(0, selectionStart - 1) + currentValue.slice(selectionEnd);
      const newCursorPos = selectionStart !== selectionEnd ? selectionStart : selectionStart - 1;
      handleChangeValue == null ? void 0 : handleChangeValue(name, newValue);
      requestAnimationFrame(() => {
        target.setSelectionRange(newCursorPos, newCursorPos);
      });
    }
  };
  return /* @__PURE__ */ React16.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      onClick: triggerFocus,
      ref,
      width
    },
    !showInput ? /* @__PURE__ */ React16.createElement(React16.Fragment, null, /* @__PURE__ */ React16.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React16.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React16.createElement(InvisibleComponent, null, /* @__PURE__ */ React16.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        type: "text",
        value
      }, props), {
        onBlur: removeFocus,
        onChange: handleChange,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown
      })
    )), /* @__PURE__ */ React16.createElement(PremiumUnitComponent, null, units)) : /* @__PURE__ */ React16.createElement(React16.Fragment, null, /* @__PURE__ */ React16.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React16.createElement(PremiumLabelSmallComponent, null, label, isRequired ? /* @__PURE__ */ React16.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React16.createElement(ValueComponent, null, /* @__PURE__ */ React16.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        placeholder: "--:--",
        ref: inputRef,
        type: "text",
        value
      }, props), {
        onBlur: removeFocus,
        onChange: handleChange,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown
      })
    ))), units ? /* @__PURE__ */ React16.createElement(UnitsForLabelAndValueComponent, null, /* @__PURE__ */ React16.createElement(PremiumUnitComponent, null, units)) : null)
  );
}

// src/components/input/types/number-input.tsx
import React17, { useEffect as useEffect8, useRef as useRef9, useState as useState11 } from "react";
function NumberInput(_a) {
  var _b = _a, {
    label,
    units,
    name,
    value,
    hasError,
    isDisabled,
    isRequired,
    handleChangeValue,
    width,
    min = 1
  } = _b, props = __objRest(_b, [
    "label",
    "units",
    "name",
    "value",
    "hasError",
    "isDisabled",
    "isRequired",
    "handleChangeValue",
    "width",
    "min"
  ]);
  const [hasFocus, setHasFocus] = useState11(false);
  const inputRef = useRef9(null);
  const { setRef } = useDisableInputScrollHook(inputRef);
  const hasValue = Boolean(value) && value !== "" && (Number(value) !== 0 || Number(value) >= min);
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const triggerFocus = () => {
    if (isDisabled)
      return;
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  useEffect8(() => {
    setRef(inputRef);
  }, [inputRef, setRef]);
  const handleChange = (event) => {
    const newValue = event.target.value;
    let formattedText = "";
    const values = newValue.split("");
    let transition = "";
    const expression = /(?:^\d+$)|(?:^\d+\.\d+$)|[,.]/;
    const finalExpression = new RegExp(`^(?:[${min}-9][0-9]*[,.]?[0-9]{0,3})$`);
    for (const char of values) {
      if (expression.test(char) && char !== "") {
        transition += char.replace(",", ".");
        if (finalExpression.test(transition)) {
          formattedText += char.replace(",", ".");
        }
      }
    }
    if (handleChangeValue) {
      handleChangeValue(name, formattedText);
    }
  };
  const handleKeyDown = (event) => {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  };
  return /* @__PURE__ */ React17.createElement(
    PremiumInputContainer,
    {
      hasError,
      hasFocus,
      isDisabled,
      onClick: triggerFocus,
      ref,
      width
    },
    !showInput ? /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React17.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React17.createElement(InvisibleComponent, null, /* @__PURE__ */ React17.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        ref: inputRef,
        type: "text",
        value
      }, props), {
        onChange: handleChange,
        onFocus: triggerFocus
      })
    )), /* @__PURE__ */ React17.createElement(PremiumUnitComponent, null, units)) : /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React17.createElement(PremiumLabelSmallComponent, null, label, isRequired ? /* @__PURE__ */ React17.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React17.createElement(ValueComponent, null, /* @__PURE__ */ React17.createElement(
      "input",
      __spreadProps(__spreadValues({
        ref: inputRef,
        value
      }, props), {
        disabled: isDisabled,
        onBlur: () => {
          setHasFocus(false);
        },
        onChange: handleChange,
        onFocus: () => {
          setHasFocus(true);
        },
        onKeyDown: handleKeyDown,
        type: "number"
      })
    ))), units ? /* @__PURE__ */ React17.createElement(UnitsForLabelAndValueComponent, null, /* @__PURE__ */ React17.createElement(PremiumUnitComponent, null, units)) : null)
  );
}

// src/components/input/types/checkbox-input.tsx
import React18 from "react";
import styled36 from "styled-components";
var CheckboxInputVariants = {
  primary: "primary",
  secondary: "secondary"
};
var PremiumLabelBigComponentStyled = styled36(PremiumLabelBigComponent)`
  display: flex;
  align-items: center;
  gap: 8px;
`;
var CheckboxRoot = styled36.input`
  appearance: none;
  width: var(--otk-ui-checkbox-size, 20px);
  height: var(--otk-ui-checkbox-size, 20px);
  border: var(--otk-ui-checkbox-border);
  border-radius: var(--otk-ui-checkbox-border-radius, 4px);
  cursor: pointer;

  &:checked {
    background-color: ${({ variant }) => variant === CheckboxInputVariants.primary ? "var(--otk-ui-checkbox-primary-is-checked-background-color, #007bff)" : "var(--otk-ui-checkbox-is-checked-background-color, #758ca3)"};
    border-color: ${({ variant }) => variant === CheckboxInputVariants.primary ? "var(--otk-ui-checkbox-primary-is-checked-border-color, #007bff)" : "var(--otk-ui-checkbox-is-checked-border-color, #758ca3)"};
    position: relative;

    &:after {
      content: '';
      position: absolute;
      left: var(--otk-ui-checkbox-is-checked-after-left, 5px);
      top: var(--otk-ui-checkbox-is-checked-after-top, 1px);
      width: var(--otk-ui-checkbox-is-checked-after-width, 5px);
      height: var(--otk-ui-checkbox-is-checked-after-height, 10px);
      border: ${({ variant }) => variant === CheckboxInputVariants.primary ? "solid var(--otk-ui-checkbox-primary-is-checked-after-border-color, #007bff)" : "solid var(--otk-ui-checkbox-is-checked-after-border-color,white)"};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  &:hover {
    border-color: var(--otk-ui-checkbox-is-hover-border-color, #007bff);
  }

  &:disabled {
    background-color: var(--otk-ui-checkbox-disabled-background-color, #e9ecef);
    border-color: var(--otk-ui-checkbox-disabled-border-color, #ccc);
    cursor: not-allowed;
  }
`;
function CheckboxInput({
  label,
  name,
  value,
  isChecked = false,
  isDisabled,
  variant,
  handleChangeValue,
  onToggleCheck
}) {
  return /* @__PURE__ */ React18.createElement(PremiumLabelBigComponentStyled, null, label, /* @__PURE__ */ React18.createElement(
    CheckboxRoot,
    {
      checked: isChecked ? isChecked : value === "true",
      disabled: isDisabled,
      onChange: (e) => {
        const newValue = e.target.checked ? "true" : "false";
        if (handleChangeValue) {
          handleChangeValue(name, newValue);
        }
        if (onToggleCheck) {
          onToggleCheck(name, e.target.checked);
        }
      },
      type: "checkbox",
      variant
    }
  ));
}

// src/components/input/radio-input/radios-input-component.tsx
import React20 from "react";
import styled39 from "styled-components";

// src/components/legend/legend-component.tsx
import styled37 from "styled-components";
var LegendComponent = styled37.legend`
  font-family: var(--otk-theme-font-family-primary, sans-serif);
  font-size: var(--otk-ui-radios-legend-font-size, 14px);
  font-weight: var(--otk-ui-radios-legend-font-weight, 500);
  color: var(--otk-ui-radios-legend-color, #54708c);
  margin-bottom: var(--otk-ui-radios-legend-margin-bottom, 12px);
  padding: 0;
`;

// src/components/input/radio-input/radio-input-component.tsx
import React19 from "react";
import styled38 from "styled-components";
var PremiumLabelBigComponentStyled2 = styled38(PremiumLabelBigComponent)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--otk-ui-radio-gap, 8px);
  cursor: pointer;
`;
var RadioInputRootComponent = styled38.input`
  appearance: none;
  width: var(--otk-ui-radio-size, 20px);
  height: var(--otk-ui-radio-size, 20px);
  border: var(--otk-ui-radio-border, 2px solid #94a6b8);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  background-color: var(--otk-ui-radio-background-color, #fff);
  transition: all 0.2s ease;

  &:checked {
    border-color: var(--otk-ui-radio-checked-border-color, #39a0df);
    background-color: var(--otk-ui-radio-is-checked-background-color, #fff);

    &:after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: var(--otk-ui-radio-is-checked-dot-size, 8px);
      height: var(--otk-ui-radio-is-checked-dot-size, 8px);
      background-color: var(--otk-ui-radio-is-checked-dot-color, #39a0df);
      border-radius: 50%;
    }
  }

  &:hover:not(:disabled) {
    border-color: var(--otk-ui-radio-hover-border-color, #39a0df);
    box-shadow: var(--otk-ui-radio-hover-shadow, 0 0 0 2px rgba(57, 160, 223, 0.1));
  }

  &:focus {
    outline: none;
    border-color: var(--otk-ui-radio-focus-border-color, #39a0df);
    box-shadow: var(--otk-ui-radio-focus-shadow, 0 0 0 2px rgba(57, 160, 223, 0.2));
  }

  &:disabled {
    background-color: var(--otk-ui-radio-disabled-background-color, #f2f5f8);
    border-color: var(--otk-ui-radio-disabled-border-color, #ccd6e0);
    cursor: not-allowed;
    opacity: 0.6;

    &:checked:after {
      background-color: var(--otk-ui-radio-disabled-dot-color, #94a6b8);
    }
  }
`;
function RadioInputComponent(_a) {
  var _b = _a, {
    label,
    name,
    value,
    isDisabled,
    handleChangeValue
  } = _b, props = __objRest(_b, [
    "label",
    "name",
    "value",
    "isDisabled",
    "handleChangeValue"
  ]);
  return /* @__PURE__ */ React19.createElement(PremiumLabelBigComponentStyled2, null, /* @__PURE__ */ React19.createElement(
    RadioInputRootComponent,
    __spreadValues({
      checked: value === "true",
      disabled: isDisabled,
      name,
      onChange: () => {
        if (!isDisabled) {
          handleChangeValue == null ? void 0 : handleChangeValue(name, "true");
        }
      },
      type: "radio"
    }, props)
  ), label ? /* @__PURE__ */ React19.createElement(TextComponent, null, label) : null);
}

// src/components/input/radio-input/radios-input-component.tsx
var RadiosInputRootComponent = styled39.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;
var RadiosGroupComponent = styled39(FlexComponent)`
  display: flex;
  flex-direction: ${({ layout }) => layout === "horizontal" ? "row" : "column"};
  gap: var(--otk-ui-radios-gap, 16px);
  flex-wrap: ${({ layout }) => layout === "horizontal" ? "wrap" : "nowrap"};
  align-items: ${({ layout }) => layout === "horizontal" ? "center" : "flex-start"};
`;
function RadiosInputComponent(_a) {
  var _b = _a, {
    name,
    options,
    value,
    legend,
    layout = "vertical",
    handleChangeValue
  } = _b, props = __objRest(_b, [
    "name",
    "options",
    "value",
    "legend",
    "layout",
    "handleChangeValue"
  ]);
  return /* @__PURE__ */ React20.createElement(RadiosInputRootComponent, __spreadValues({}, props), legend ? /* @__PURE__ */ React20.createElement(LegendComponent, null, legend) : null, /* @__PURE__ */ React20.createElement(RadiosGroupComponent, { layout }, options.map((option) => /* @__PURE__ */ React20.createElement(
    RadioInputComponent,
    {
      handleChangeValue,
      isDisabled: option.isDisabled,
      key: option.value,
      label: option.label,
      name: `${name}-${option.value}`,
      value: value === option.value ? "true" : "false"
    }
  ))));
}

// src/components/input/textarea/textarea-input-component.tsx
import React21, { useRef as useRef10, useState as useState12 } from "react";
function TextareaComponent(_a) {
  var _b = _a, {
    autoCapitalize,
    autoComplete,
    onChange,
    handleKeyDown,
    id,
    inputRef,
    isDisabled = false,
    removeFocus,
    triggerFocus,
    children,
    width = "100%",
    height = "var(--otk-ui-textarea-height, 100px)"
  } = _b, props = __objRest(_b, [
    "autoCapitalize",
    "autoComplete",
    "onChange",
    "handleKeyDown",
    "id",
    "inputRef",
    "isDisabled",
    "removeFocus",
    "triggerFocus",
    "children",
    "width",
    "height"
  ]);
  return /* @__PURE__ */ React21.createElement(
    "textarea",
    __spreadProps(__spreadValues({
      disabled: isDisabled,
      id,
      onBlur: removeFocus,
      onChange,
      onFocus: triggerFocus,
      onKeyDown: handleKeyDown,
      ref: inputRef,
      style: {
        display: "block",
        width,
        height,
        overflow: "auto",
        resize: "none",
        border: "none",
        outline: "none"
      }
    }, props), {
      autoCapitalize,
      autoComplete
    }),
    children
  );
}
function TextareaInputComponent(_a) {
  var _b = _a, {
    autocapitalize = "off",
    autocomplete = "off",
    onChange,
    hasError = false,
    isDisabled = false,
    isRequired = false,
    label,
    name,
    value = "",
    width = "100%",
    height = "var(--otk-ui-textarea-height, 100px)"
  } = _b, props = __objRest(_b, [
    "autocapitalize",
    "autocomplete",
    "onChange",
    "hasError",
    "isDisabled",
    "isRequired",
    "label",
    "name",
    "value",
    "width",
    "height"
  ]);
  const id = name;
  const [hasFocus, setHasFocus] = useState12(false);
  const hasValue = Boolean(value) && value !== "";
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const inputRef = useRef10(null);
  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === "Tab") {
      setHasFocus(false);
    }
  };
  const triggerFocus = () => {
    if (isDisabled) {
      return;
    }
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  const removeFocus = () => {
    setHasFocus(false);
  };
  const handleChange = (event) => {
    const newValue = event.target.value;
    if (onChange) {
      onChange(name, newValue);
    }
  };
  return /* @__PURE__ */ React21.createElement(
    PremiumInputContainer,
    {
      alignItems: "stretch",
      hasError,
      hasFocus,
      height: `calc(${height} + 50px)`,
      isDisabled,
      onClick: triggerFocus,
      ref,
      style: {
        overflow: "hidden",
        resize: "both"
      },
      width
    },
    !showInput ? /* @__PURE__ */ React21.createElement(React21.Fragment, null, /* @__PURE__ */ React21.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React21.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React21.createElement(InvisibleComponent, null, /* @__PURE__ */ React21.createElement(
      TextareaComponent,
      __spreadValues({
        autoCapitalize: autocapitalize,
        autoComplete: autocomplete,
        handleKeyDown,
        height,
        id,
        inputRef,
        isDisabled,
        onChange: handleChange,
        removeFocus,
        triggerFocus
      }, props)
    ))) : /* @__PURE__ */ React21.createElement(LabelAndValueComponent, { justifyContent: "flex-start" }, /* @__PURE__ */ React21.createElement(PremiumLabelSmallComponent, { htmlFor: name }, label, isRequired ? /* @__PURE__ */ React21.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React21.createElement(ValueComponent, null, /* @__PURE__ */ React21.createElement(
      TextareaComponent,
      __spreadValues({
        autoCapitalize: autocapitalize,
        autoComplete: autocomplete,
        handleKeyDown,
        height,
        id,
        inputRef,
        isDisabled,
        onChange: handleChange,
        removeFocus,
        triggerFocus
      }, props)
    )))
  );
}

// src/components/input/input.tsx
var inputTypes = {
  text: "text",
  textWithIcon: "text-with-icon",
  phone: "phone",
  rating: "rating",
  select: "select",
  search: "search",
  searchWithInternalSelect: "select-with-internal-search",
  password: "password",
  date: "date",
  time: "time",
  number: "number",
  checkbox: "checkbox",
  radio: "radio",
  textArea: "textarea"
};
function Input(_a) {
  var _b = _a, {
    datePickerClassName,
    handleChangeEvent,
    handleChangeValue,
    hasError,
    highlightDatepickerDates,
    includedDatepickerDates,
    isDisabled,
    name,
    onChangeDate,
    options = [],
    portal,
    selectedDatepickerDate,
    shouldCloseDatepickerOnSelect = true,
    showOnlyDatePicker,
    type,
    width
  } = _b, props = __objRest(_b, [
    "datePickerClassName",
    "handleChangeEvent",
    "handleChangeValue",
    "hasError",
    "highlightDatepickerDates",
    "includedDatepickerDates",
    "isDisabled",
    "name",
    "onChangeDate",
    "options",
    "portal",
    "selectedDatepickerDate",
    "shouldCloseDatepickerOnSelect",
    "showOnlyDatePicker",
    "type",
    "width"
  ]);
  if (type === inputTypes.phone) {
    return /* @__PURE__ */ React22.createElement(
      PhoneInput,
      __spreadProps(__spreadValues({
        handleChangeValue,
        name
      }, props), {
        hasError
      })
    );
  }
  if (type === inputTypes.rating) {
    return /* @__PURE__ */ React22.createElement(RatingInput, __spreadValues({ handleChangeEvent, name }, props));
  }
  if (type === inputTypes.select) {
    return /* @__PURE__ */ React22.createElement(
      SelectInputComponent,
      __spreadValues({
        handleChangeValue,
        hasError,
        name,
        options,
        portal,
        width
      }, props)
    );
  }
  if (type === inputTypes.searchWithInternalSelect) {
    return /* @__PURE__ */ React22.createElement(
      SelectWithInternalSearchInputComponent,
      __spreadValues({
        handleChangeValue,
        name,
        options,
        width
      }, props)
    );
  }
  if (type === inputTypes.search) {
    return /* @__PURE__ */ React22.createElement(
      TextInputWithIconComponent,
      __spreadValues({
        handleChangeEvent,
        icon: /* @__PURE__ */ React22.createElement(SearchIcon2, null),
        name,
        width
      }, props)
    );
  }
  if (type === inputTypes.textWithIcon) {
    return /* @__PURE__ */ React22.createElement(
      TextInputWithIconComponent,
      __spreadValues({
        handleChangeEvent,
        isDisabled,
        name,
        width
      }, props)
    );
  }
  if (type === inputTypes.date) {
    return /* @__PURE__ */ React22.createElement(
      DateInput,
      __spreadProps(__spreadValues({
        datePickerClassName,
        handleChangeValue,
        hasError,
        highlightDatepickerDates,
        isDisabled,
        name,
        onChangeDate,
        selectedDatepickerDate,
        shouldCloseOnSelect: shouldCloseDatepickerOnSelect,
        showOnlyDatePicker,
        width
      }, props), {
        includedDatepickerDates
      })
    );
  }
  if (type === inputTypes.time) {
    return /* @__PURE__ */ React22.createElement(
      TimeInputComponent,
      __spreadProps(__spreadValues({
        handleChangeValue,
        name,
        width
      }, props), {
        hasError,
        isDisabled
      })
    );
  }
  if (type === inputTypes.number) {
    return /* @__PURE__ */ React22.createElement(
      NumberInput,
      __spreadProps(__spreadValues({
        handleChangeValue,
        name,
        width
      }, props), {
        hasError,
        isDisabled
      })
    );
  }
  if (type === inputTypes.checkbox) {
    return /* @__PURE__ */ React22.createElement(
      CheckboxInput,
      __spreadProps(__spreadValues({
        handleChangeValue,
        name
      }, props), {
        isDisabled
      })
    );
  }
  if (type === inputTypes.radio) {
    return /* @__PURE__ */ React22.createElement(
      RadioInputComponent,
      __spreadProps(__spreadValues({
        handleChangeValue,
        name
      }, props), {
        isDisabled
      })
    );
  }
  if (type === inputTypes.textArea) {
    const _a2 = props, { onChange: _ } = _a2, textareaProps = __objRest(_a2, ["onChange"]);
    return /* @__PURE__ */ React22.createElement(
      TextareaInputComponent,
      __spreadValues({
        hasError,
        isDisabled,
        name,
        onChange: handleChangeValue,
        width
      }, textareaProps)
    );
  }
  return /* @__PURE__ */ React22.createElement(
    TextInput,
    __spreadValues({
      handleChangeEvent,
      hasError,
      isDisabled,
      name,
      type,
      width
    }, props)
  );
}

// src/components/input/types/semaphore-input-component.tsx
import React25, { useMemo as useMemo2 } from "react";
import styled41 from "styled-components";
import { IconComponent } from "@ontruck/fe-icons";

// src/components/spinners/boxes-spinner-component.tsx
import React23 from "react";
function BoxesSpinnerComponent(_a) {
  var _b = _a, {
    sizeInPixels = 24,
    color
  } = _b, props = __objRest(_b, [
    "sizeInPixels",
    "color"
  ]);
  return /* @__PURE__ */ React23.createElement(
    "svg",
    __spreadValues({
      height: sizeInPixels,
      viewBox: "0 0 24 24",
      width: sizeInPixels,
      xmlns: "http://www.w3.org/2000/svg"
    }, props),
    /* @__PURE__ */ React23.createElement(
      "path",
      {
        d: "M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z",
        fill: color || "#000"
      },
      /* @__PURE__ */ React23.createElement(
        "animateTransform",
        {
          attributeName: "transform",
          dur: "0.75s",
          repeatCount: "indefinite",
          type: "rotate",
          values: "0 12 12;360 12 12"
        }
      )
    )
  );
}

// src/components/spinners/spinner-component.tsx
import React24 from "react";
import styled40, { keyframes } from "styled-components";
var spinnerVariants = {
  primary: "primary",
  secondary: "secondary"
};
var spinnerSizes = {
  xs: "xs",
  s: "s",
  m: "m"
};
var rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
var getColorValue = (color, variant) => {
  if (color) {
    return color;
  }
  switch (variant) {
    case spinnerVariants.primary:
      return "var(--otk-ui-spinner-primary-color, #2396DC)";
    case spinnerVariants.secondary:
      return "var(--otk-ui-spinner-secondary-color, #ffcc00)";
    default:
      return "var(--otk-ui-spinner-color, #94A6B8)";
  }
};
var getSizeValue = (sizeInPixels, size) => {
  if (sizeInPixels) {
    return sizeInPixels;
  }
  switch (size) {
    case spinnerSizes.xs:
      return 8;
    case spinnerSizes.s:
      return 16;
    case spinnerSizes.m:
      return 24;
    default:
      return 24;
  }
};
var SpinnerSvg = styled40.svg`
  animation: ${rotate} 1s linear infinite;
  width: ${({ sizeInPixels = 24 }) => sizeInPixels}px;
  height: ${({ sizeInPixels = 24 }) => sizeInPixels}px;
`;
var SpinnerCircle = styled40.circle`
  fill: none;
  stroke: ${({ color }) => getColorValue(color)};
  stroke-width: ${({ strokeWidth = 2 }) => strokeWidth};
  stroke-linecap: round;
  stroke-dasharray: 31.416; /* 2 * PI * 5 (radius) */
  stroke-dashoffset: 31.416;
  animation: ${keyframes`
    0% {
      stroke-dasharray: 0 31.416;
    }
    50% {
      stroke-dasharray: 15.708 15.708;
    }
    100% {
      stroke-dasharray: 31.416 0;
    }
  `} 1.5s ease-in-out infinite;
`;
var SpinnerContainer = styled40.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ variant, color, sizeInPixels = 24 }) => {
  if (variant === "secondary") {
    return `
        padding: ${sizeInPixels * 0.25}px;
        border-radius: 50%;
        background-color: ${getColorValue(color)}20;
      `;
  }
  return "";
}}
`;
function SpinnerComponent(_a) {
  var _b = _a, {
    variant = spinnerVariants.primary,
    color,
    sizeInPixels,
    strokeWidth = 2,
    className,
    size = spinnerSizes.m
  } = _b, props = __objRest(_b, [
    "variant",
    "color",
    "sizeInPixels",
    "strokeWidth",
    "className",
    "size"
  ]);
  const radius = 5;
  const viewBoxSize = 12;
  const sizeValueInPixels = getSizeValue(sizeInPixels, size);
  return /* @__PURE__ */ React24.createElement(
    SpinnerContainer,
    __spreadValues({
      className,
      color,
      sizeInPixels: sizeValueInPixels,
      variant
    }, props),
    /* @__PURE__ */ React24.createElement(
      SpinnerSvg,
      {
        sizeInPixels: sizeValueInPixels,
        viewBox: `0 0 ${viewBoxSize} ${viewBoxSize}`,
        xmlns: "http://www.w3.org/2000/svg"
      },
      /* @__PURE__ */ React24.createElement(
        SpinnerCircle,
        {
          color,
          cx: viewBoxSize / 2,
          cy: viewBoxSize / 2,
          r: radius,
          strokeWidth
        }
      )
    )
  );
}

// src/components/input/types/semaphore-input-component.tsx
var SemaphoreInputSpinnerComponent = styled41(BoxesSpinnerComponent)`
  margin-right: 10px;

  path {
    fill: var(--otk-theme-color-grey-700, #758ca3);
  }
`;
var SemaphoreInputLightComponent = styled41.div`
  background-color: ${({ color }) => color || "var(--otk-theme-color-grey-700, #758ca3)"};
  width: 7px;
  height: 7px;
`;
var SemaphoreInputLevelComponent = styled41.div`
  background-color: ${({ color }) => color || "var(--otk-theme-color-grey-700, #758ca3)"};
  width: 100%;
  height: 4px;
`;
var semaphoreLevels = {
  high: "good",
  medium: "regular",
  low: "bad",
  none: ""
};
function SemaphoreInputComponent({
  label,
  hasError = false,
  isLoading,
  level,
  value,
  width,
  labelFontSize
}) {
  const color = useMemo2(() => {
    if (level === semaphoreLevels.low) {
      return "var(--otk-theme-color-error-900, #cc000a)";
    }
    if (level === semaphoreLevels.medium) {
      return "var(--otk-theme-color-warning-800, #fcb605)";
    }
    if (level === semaphoreLevels.high) {
      return "var(--otk-theme-color-success-800, #00b573)";
    }
    return "var(--otk-theme-color-grey-700, #758ca3)";
  }, [level]);
  const count = useMemo2(() => {
    if (level === semaphoreLevels.low) {
      return 1;
    }
    if (level === semaphoreLevels.medium) {
      return 2;
    }
    if (level === semaphoreLevels.high) {
      return 3;
    }
    return 0;
  }, [level]);
  return /* @__PURE__ */ React25.createElement(
    PremiumInputContainer,
    {
      hasError,
      isDisabled: true,
      isReadOnly: true,
      overflow: "hidden",
      padding: "0",
      width
    },
    /* @__PURE__ */ React25.createElement(FlexComponent, { alignItems: "center", height: "100%", justifyContent: "flex-end", width: "100%" }, hasError ? /* @__PURE__ */ React25.createElement(FlexComponent, { alignItems: "center", height: "100%", justifyContent: "center", width: "100%" }, /* @__PURE__ */ React25.createElement(
      IconComponent,
      {
        color: "var(--otk-theme-color-error-900, #cc000a)",
        name: "circle-exclamation",
        sizeInPixels: 20
      }
    )) : null, isLoading && !hasError ? /* @__PURE__ */ React25.createElement(SemaphoreInputSpinnerComponent, { sizeInPixels: 20 }) : null, !isLoading && !hasError ? /* @__PURE__ */ React25.createElement(FlexComponent, { flexDirection: "column", height: "100%", width: "100%" }, /* @__PURE__ */ React25.createElement(FlexComponent, { flexDirection: "row", height: "100%", width: "100%" }, label ? /* @__PURE__ */ React25.createElement(
      FlexComponent,
      {
        alignItems: "center",
        height: "100%",
        justifyContent: "flex-start",
        margin: "0 0 0 8px",
        width: "80%"
      },
      /* @__PURE__ */ React25.createElement(
        TextComponent,
        {
          fontSize: labelFontSize ? labelFontSize : void 0,
          size: labelFontSize ? void 0 : "s"
        },
        label
      )
    ) : null, /* @__PURE__ */ React25.createElement(
      FlexComponent,
      {
        alignItems: "center",
        flexDirection: "column",
        height: "100%",
        justifyContent: "flex-end",
        width: "100%"
      },
      /* @__PURE__ */ React25.createElement(
        FlexComponent,
        {
          alignItems: "center",
          gap: "10px",
          height: "100%",
          justifyContent: "flex-end",
          width: "100%"
        },
        /* @__PURE__ */ React25.createElement(TextComponent, null, value),
        /* @__PURE__ */ React25.createElement(FlexComponent, { flexDirection: "column", gap: "2px", margin: "0 5px 0 0" }, Array(3 - count).fill(null).map((_, index) => {
          const semaphoreLightKey = index + 1;
          return /* @__PURE__ */ React25.createElement(
            SemaphoreInputLightComponent,
            {
              color: "var(--otk-theme-color-grey-500, #94A6B8)",
              key: semaphoreLightKey
            }
          );
        }), count > 0 ? Array(count).fill(null).map((_, index) => {
          const semaphoreLightKey = index + 1;
          return /* @__PURE__ */ React25.createElement(SemaphoreInputLightComponent, { color, key: semaphoreLightKey });
        }) : null)
      )
    )), /* @__PURE__ */ React25.createElement(SemaphoreInputLevelComponent, { color })) : null)
  );
}

// src/components/input/multi-select/multi-select-input-component.tsx
import React28, { useMemo as useMemo4, useState as useState14, useCallback, useEffect as useEffect10 } from "react";
import styled44 from "styled-components";

// src/components/input/multi-select/multi-select-badge-component.tsx
import React27 from "react";
import { IconComponent as IconComponent2 } from "@ontruck/fe-icons";

// src/components/buttons/button-container.tsx
import styled42 from "styled-components";
var ButtonContainer = styled42.div`
  display: flex;
  width: 600px;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
`;

// src/components/buttons/button-group-component.tsx
import React26, { useReducer, useMemo as useMemo3 } from "react";
import styled43 from "styled-components";
var ButtonGroupWrapperComponent = styled43.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  ${ButtonComponent} {
    margin: 0;

    &:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    &:not(:last-child):not(:first-child) {
      border-radius: 0;
    }

    &:not(.active) {
      border: 1px solid var(--otk-theme-color-grey-300, #ccd6e0);
      background-color: var(--otk-ui-button-group-background, #e2e8ee);
      color: var(--otk-ui-button-group-color, #182a33);
    }
  }
`;
var groupStateActionType = {
  updateButtonDisabledState: "update_button_disabled_state",
  updateButtonActiveState: "update_button_active_state"
};
function ButtonGroupComponent({
  buttons,
  size = buttonSizes.m
}) {
  const groupStateReducer = (state, action) => {
    if (action.type === groupStateActionType.updateButtonActiveState) {
      const newGroupState = state.map((b) => {
        if (b.id === action.payload.id) {
          return __spreadProps(__spreadValues({}, b), {
            isActive: action.payload.isActive
          });
        }
        return __spreadProps(__spreadValues({}, b), {
          isActive: false
        });
      });
      return newGroupState;
    }
    return state;
  };
  const groupStateInitialState = useMemo3(
    () => buttons.map((button) => ({
      id: button.id,
      isActive: button.isActive
    })),
    [buttons]
  );
  const [groupState, groupStateDispatch] = useReducer(groupStateReducer, groupStateInitialState);
  const getIsActive = (id) => {
    if (groupState.length === 0) {
      return false;
    }
    const buttonState = groupState.find((b) => b.id === id);
    return buttonState ? Boolean(buttonState.isActive) : false;
  };
  const handleClick = (id) => {
    if (groupState.length === 0) {
      return;
    }
    const buttonState = groupState.find((b) => b.id === id);
    if (buttonState) {
      groupStateDispatch({
        type: groupStateActionType.updateButtonActiveState,
        payload: { id, isActive: true }
      });
    }
  };
  if (!buttons.length && buttons.length === 0) {
    return null;
  }
  return /* @__PURE__ */ React26.createElement(ButtonGroupWrapperComponent, null, /* @__PURE__ */ React26.createElement(FlexComponent, null, buttons.map((b) => /* @__PURE__ */ React26.createElement(
    ButtonComponent,
    {
      className: getIsActive(b.id) ? "active" : "",
      color: "primary",
      disabled: b.isDisabled,
      key: b.id,
      onClick: () => {
        handleClick(b.id);
        if (b.onClick) {
          b.onClick();
        }
      },
      size
    },
    b.label
  ))));
}

// src/components/input/multi-select/multi-select-badge-component.tsx
function MultiSelectBadgeComponent({
  label,
  onClose
}) {
  return /* @__PURE__ */ React27.createElement(
    BadgeComponent,
    {
      borderRadius: "var(--otk-ui-badge-border-radius)",
      padding: "var(--otk-theme-space-xs) var(--otk-theme-space-s) var(--otk-theme-space-xs) var(--otk-theme-space-s)"
    },
    /* @__PURE__ */ React27.createElement(
      FlexComponent,
      {
        alignItems: "stretch",
        gap: "var(--otk-theme-space-xs)",
        justifyContent: "space-between",
        width: "100%"
      },
      label,
      /* @__PURE__ */ React27.createElement(ButtonComponent, { onClick: onClose, size: "s", variant: "icon" }, /* @__PURE__ */ React27.createElement(
        FlexComponent,
        {
          alignItems: "start",
          gap: "var(--otk-theme-space-xs)",
          justifyContent: "center",
          width: "100%"
        },
        /* @__PURE__ */ React27.createElement(IconComponent2, { color: "var(--otk-ui-badge-color)", name: "close", size: "2xs" })
      ))
    )
  );
}

// src/components/input/multi-select/filter-options-match-by-text-hook.ts
import { useEffect as useEffect9, useState as useState13 } from "react";
function getNormalizedTextToCompare(text) {
  return text.toUpperCase().normalize("NFD").replace(new RegExp("\\p{Diacritic}", "gu"), "");
}
function getIsMatch(source, text) {
  return getNormalizedTextToCompare(source).includes(getNormalizedTextToCompare(text));
}
function useFilterOptionsMatchByTextHook(text, options) {
  const [optionsMatchByText, setOptionsMatchByText] = useState13([]);
  useEffect9(() => {
    const optionsMatch = options.filter((option) => getIsMatch(option.label, text));
    if (optionsMatch.length > 0) {
      setOptionsMatchByText(optionsMatch);
    } else {
      setOptionsMatchByText([]);
    }
  }, [text, options]);
  return optionsMatchByText;
}

// src/components/input/multi-select/multi-select-input-component.tsx
var MultiSelectSearchWrapperComponent = styled44.div`
  width: 100%;

  ${PremiumInputContainer} {
    border: 1px solid var(--otk-theme-color-grey-300);
  }
`;
var MultiSelectValueComponent = styled44(ValueComponent)`
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${({ width }) => width ? `calc(${width} - 20px)` : "auto"};
`;
var MultiSelectLabelAndValueComponent = styled44(LabelAndValueComponent)`
  width: 95%;
`;
var MultiSelectLabelComponent = styled44(LabelAndValueComponent)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${({ width }) => width ? `calc(${width} - 20px)` : "auto"};
  height: 16px;
`;
function MultiSelectInputComponent({
  className,
  growTop = false,
  onChange,
  hasError = false,
  isDisabled = false,
  isRequired = false,
  label,
  name = "",
  options = [],
  values,
  width,
  labelSelected = "items selected",
  searchable = false
}) {
  const [search2, setSearch] = useState14("");
  const [selectedValues, setSelectedValues] = useState14(
    values ? new Set(values) : void 0
  );
  useEffect10(() => {
    const areValuesEqual = values == null ? void 0 : values.every((value) => selectedValues == null ? void 0 : selectedValues.has(value));
    if (!values && selectedValues && selectedValues.size > 0) {
      setSelectedValues(void 0);
      return;
    }
    if (values && values.length > 0 && !areValuesEqual) {
      setSelectedValues(new Set(values));
    }
  }, [values, selectedValues, setSelectedValues]);
  const [hasFocus, setHasFocus] = useState14(false);
  const hasValues = selectedValues ? selectedValues.size > 0 : false;
  const debouncedSearch = useDebounceHook(search2, 200);
  const optionsMatchByText = useFilterOptionsMatchByTextHook(debouncedSearch, options);
  const clickRefFrom = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const handleChange = (selectedValue) => {
    if (isDisabled) {
      return;
    }
    if (!selectedValues) {
      setSelectedValues(/* @__PURE__ */ new Set([selectedValue]));
      if (onChange) {
        onChange([selectedValue]);
      }
      return;
    }
    if (!selectedValues.has(selectedValue)) {
      setSelectedValues(/* @__PURE__ */ new Set([...Array.from(selectedValues), selectedValue]));
      if (onChange) {
        onChange([...Array.from(selectedValues), selectedValue]);
      }
    }
  };
  const labelComponent = useMemo4(() => {
    if (label) {
      const LabelWrapperComponent = selectedValues ? PremiumLabelSmallComponent : PremiumLabelBigComponent;
      return /* @__PURE__ */ React28.createElement(LabelWrapperComponent, null, /* @__PURE__ */ React28.createElement(MultiSelectLabelComponent, { width }, label), isRequired ? /* @__PURE__ */ React28.createElement(RequiredIndicatorComponent, null) : null);
    }
    return null;
  }, [label, selectedValues, isRequired, width]);
  const handleClose = (unSelectedValue) => {
    if (onChange) {
      if (!selectedValues) {
        return;
      }
      const updatedSelectedValues = Array.from(selectedValues).filter(
        (item) => item !== unSelectedValue
      );
      setSelectedValues(new Set(updatedSelectedValues));
      onChange(updatedSelectedValues);
    }
  };
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  const getSelectedComponentHeight = useCallback(() => {
    if (!selectedValues && !searchable) {
      return "0px";
    }
    if (selectedValues && selectedValues.size > 0) {
      return "auto";
    }
    return "auto";
  }, [selectedValues, searchable]);
  const getSelectedComponentPadding = useCallback(() => {
    if (!selectedValues && !searchable) {
      return "unset";
    }
    if (selectedValues && selectedValues.size > 0) {
      if (searchable) {
        return "var(--otk-theme-space-s) var(--otk-theme-space-s) var(--otk-theme-space-m) var(--otk-theme-space-s)";
      }
      return "var(--otk-theme-space-s)";
    }
    return "var(--otk-theme-space-s)";
  }, [searchable, selectedValues]);
  const getSelectedComponentBorderBottom = useCallback(() => {
    if (!selectedValues && !searchable) {
      return "none";
    }
    if (selectedValues && selectedValues.size > 0) {
      return "1px solid var(--otk-theme-color-grey-300)";
    }
    return "1px solid var(--otk-theme-color-grey-300)";
  }, [searchable, selectedValues]);
  const selectedComponentHeight = useMemo4(
    () => getSelectedComponentHeight(),
    [getSelectedComponentHeight]
  );
  const selectedComponentPadding = useMemo4(
    () => getSelectedComponentPadding(),
    [getSelectedComponentPadding]
  );
  const selectedComponentBorderBottom = useMemo4(
    () => getSelectedComponentBorderBottom(),
    [getSelectedComponentBorderBottom]
  );
  return /* @__PURE__ */ React28.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      key: values == null ? void 0 : values.join(","),
      onClick: () => {
        if (isDisabled) {
          return;
        }
        setHasFocus(true);
      },
      ref: clickRefFrom,
      width
    },
    hasFocus ? /* @__PURE__ */ React28.createElement(DropdownContainer, { growTop, maxHeight: "300px", overflow: "hidden" }, /* @__PURE__ */ React28.createElement(
      FlexComponent,
      {
        backgroundColor: "var(--otk-theme-color-grey-100)",
        borderBottom: selectedComponentBorderBottom,
        flexDirection: "column",
        flexWrap: "wrap",
        gap: "var(--otk-theme-space-s)",
        height: selectedComponentHeight,
        padding: selectedComponentPadding
      },
      searchable ? /* @__PURE__ */ React28.createElement(MultiSelectSearchWrapperComponent, null, /* @__PURE__ */ React28.createElement(
        TextInput,
        {
          handleChangeEvent: handleSearchChange,
          name: "multi-select-input-search",
          type: "text",
          value: search2
        }
      )) : null,
      selectedValues ? /* @__PURE__ */ React28.createElement(
        FlexComponent,
        {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "var(--otk-theme-space-xs)",
          overflow: "auto"
        },
        Array.from(selectedValues).map((item) => {
          var _a;
          return /* @__PURE__ */ React28.createElement(
            MultiSelectBadgeComponent,
            {
              key: `multiselect-selected-badge-${item}`,
              label: (_a = options.find((option) => option.value === item)) == null ? void 0 : _a.label,
              onClose: () => {
                handleClose(item);
              }
            }
          );
        })
      ) : null
    ), optionsMatchByText.length > 0 ? /* @__PURE__ */ React28.createElement(FlexComponent, { flexDirection: "column", overflowX: "hidden", overflowY: "auto" }, optionsMatchByText.map((item) => {
      if (selectedValues == null ? void 0 : selectedValues.has(item.value)) {
        return null;
      }
      return /* @__PURE__ */ React28.createElement(
        DropdownItemContainer,
        {
          isSelected: false,
          key: item.value,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleChange(item.value);
          }
        },
        item.component ? item.component : item.label
      );
    })) : null) : null,
    /* @__PURE__ */ React28.createElement(MultiSelectLabelAndValueComponent, null, labelComponent, hasValues ? /* @__PURE__ */ React28.createElement(MultiSelectValueComponent, { width }, selectedValues && selectedValues.size > 0 ? `${selectedValues.size} ${labelSelected}` : "", /* @__PURE__ */ React28.createElement(InvisibleComponent, null, /* @__PURE__ */ React28.createElement(
      "input",
      {
        name,
        onBlur: () => {
          setHasFocus(false);
        },
        onFocus: () => {
          setHasFocus(true);
        }
      }
    ))) : null),
    /* @__PURE__ */ React28.createElement(UnitsForLabelAndValueComponent, { noPadding: true }, /* @__PURE__ */ React28.createElement(PremiumUnitComponent, null, /* @__PURE__ */ React28.createElement(
      "svg",
      {
        fill: "none",
        height: "16",
        viewBox: "0 0 16 16",
        width: "16",
        xmlns: "http://www.w3.org/2000/svg"
      },
      /* @__PURE__ */ React28.createElement(
        "path",
        {
          d: "M10.9342 6.20291C10.8435 6.07757 10.6731 6.00002 10.4884 6H5.51147C5.32689 6.00007 5.15666 6.07757 5.06599 6.20282C4.97532 6.32807 4.97827 6.48165 5.0737 6.60473L7.56218 9.80778C7.65489 9.9271 7.82091 10 7.99996 10C8.179 10 8.34502 9.9271 8.43773 9.80778L10.9262 6.60473C11.0217 6.4817 11.0247 6.32816 10.9342 6.20291Z",
          fill: "#54708C"
        }
      )
    )))
  );
}

// src/components/input/time-input-native-component/time-input-native-component.tsx
import React29, { useRef as useRef11, useState as useState15 } from "react";
import styled45 from "styled-components";
var TimeInputNativeValueComponent = styled45(ValueComponent)`
  & input[type='time']::-webkit-calendar-picker-indicator {
    display: none;
  }
`;
function TimeInputNativeComponent(_a) {
  var _b = _a, {
    className,
    handleChangeValue,
    hasError,
    isDisabled,
    isRequired,
    label,
    name,
    value = "",
    width,
    units
  } = _b, props = __objRest(_b, [
    "className",
    "handleChangeValue",
    "hasError",
    "isDisabled",
    "isRequired",
    "label",
    "name",
    "value",
    "width",
    "units"
  ]);
  const [hasFocus, setHasFocus] = useState15(false);
  const inputRef = useRef11(null);
  const hasValue = Boolean(value) && value !== "";
  const showInput = hasFocus || hasValue;
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });
  const triggerFocus = () => {
    if (isDisabled) {
      return;
    }
    setHasFocus(true);
    setTimeout(() => {
      var _a2;
      (_a2 = inputRef.current) == null ? void 0 : _a2.focus();
    }, 1);
  };
  const removeFocus = () => {
    setHasFocus(false);
  };
  const handleChange = (event) => {
    handleChangeValue == null ? void 0 : handleChangeValue(name, event.target.value);
  };
  const handleKeyDown = (event) => {
    if (event.shiftKey && event.key === "Tab") {
      setHasFocus(false);
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      const target = event.currentTarget;
      const { selectionStart, selectionEnd, value: currentValue } = target;
      if (selectionStart === null || selectionEnd === null)
        return;
      if (selectionStart === 0 && selectionStart === selectionEnd)
        return;
      const newValue = selectionStart !== selectionEnd ? currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd) : currentValue.slice(0, selectionStart - 1) + currentValue.slice(selectionEnd);
      const newCursorPos = selectionStart !== selectionEnd ? selectionStart : selectionStart - 1;
      handleChangeValue == null ? void 0 : handleChangeValue(name, newValue);
      requestAnimationFrame(() => {
        target.setSelectionRange(newCursorPos, newCursorPos);
      });
    }
  };
  return /* @__PURE__ */ React29.createElement(
    PremiumInputContainer,
    {
      className,
      hasError,
      hasFocus,
      isDisabled,
      onClick: triggerFocus,
      ref,
      width
    },
    !showInput ? /* @__PURE__ */ React29.createElement(React29.Fragment, null, /* @__PURE__ */ React29.createElement(PremiumLabelBigComponent, null, label, isRequired ? /* @__PURE__ */ React29.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React29.createElement(InvisibleComponent, null, /* @__PURE__ */ React29.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        type: "text",
        value
      }, props), {
        onBlur: removeFocus,
        onChange: handleChange,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown
      })
    )), /* @__PURE__ */ React29.createElement(PremiumUnitComponent, null, units)) : /* @__PURE__ */ React29.createElement(React29.Fragment, null, /* @__PURE__ */ React29.createElement(LabelAndValueComponent, null, /* @__PURE__ */ React29.createElement(PremiumLabelSmallComponent, null, label, isRequired ? /* @__PURE__ */ React29.createElement(RequiredIndicatorComponent, null) : null), /* @__PURE__ */ React29.createElement(TimeInputNativeValueComponent, null, /* @__PURE__ */ React29.createElement(
      "input",
      __spreadProps(__spreadValues({
        disabled: isDisabled,
        placeholder: "--:--",
        ref: inputRef,
        type: "time",
        value
      }, props), {
        onBlur: removeFocus,
        onChange: handleChange,
        onFocus: triggerFocus,
        onKeyDown: handleKeyDown
      })
    ))), units ? /* @__PURE__ */ React29.createElement(UnitsForLabelAndValueComponent, null, /* @__PURE__ */ React29.createElement(PremiumUnitComponent, null, units)) : null)
  );
}

// src/components/table/table-component.tsx
import React43, { useEffect as useEffect11 } from "react";
import styled55 from "styled-components";
import { EmptyListSearchIcon } from "@ontruck/fe-icons";

// src/components/conditional-wrapper/conditional-wrapper.tsx
import React30 from "react";
function ConditionalWrapper({
  children,
  condition,
  wrapper: Wrapper
}) {
  if (condition) {
    return /* @__PURE__ */ React30.createElement(Wrapper, null, children);
  }
  return children;
}

// src/components/table/table-pagination-component.tsx
import React32, { useMemo as useMemo5 } from "react";

// src/components/table/get-table-pagination-elements-service.ts
function getTablePaginationElementsService(currentPage, numberOfPages) {
  if (numberOfPages <= 1)
    return [];
  const firstEllipsis = { id: "first-ellipsis", value: "..." };
  const secondEllipsis = { id: "second-ellipsis", value: "..." };
  const paginationElements = [];
  const maxVisiblePages = 4;
  paginationElements.push("<");
  if (currentPage <= maxVisiblePages) {
    for (let i = 1; i <= maxVisiblePages; i++) {
      if (i <= numberOfPages) {
        paginationElements.push(i);
      }
    }
    if (numberOfPages > maxVisiblePages + 1) {
      paginationElements.push(firstEllipsis);
    }
  } else if (currentPage > numberOfPages - maxVisiblePages) {
    paginationElements.push(1);
    paginationElements.push(firstEllipsis);
    for (let i = numberOfPages - (maxVisiblePages - 1); i <= numberOfPages; i++) {
      paginationElements.push(i);
    }
  } else {
    paginationElements.push(1);
    paginationElements.push(firstEllipsis);
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      paginationElements.push(i);
    }
    paginationElements.push(secondEllipsis);
  }
  if (!paginationElements.includes(numberOfPages)) {
    paginationElements.push(numberOfPages);
  }
  paginationElements.push(">");
  return paginationElements;
}

// src/components/table/table-pagination-element-component.tsx
import styled46 from "styled-components";
var tablePaginationElementTypes = {
  arrowLeft: "arrow-left",
  arrowRight: "arrow-right",
  number: "number",
  dots: "dots"
};
var TablePaginationElementComponent = styled46.div`
  display: flex;
  width: var(--otk-ui-table-pagination-element-size, 32px);
  height: var(--otk-ui-table-pagination-element-size, 32px);
  padding: var(--otk-ui-table-pagination-element-padding, 0px 2px);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--otk-ui-table-pagination-element-gap, 10px);
  border-radius: var(--otk-ui-table-pagination-element-border-radius, 4px);
  text-align: center;
  font-family: var(--otk-ui-table-pagination-element-font-family, sans-serif);
  font-size: var(--otk-ui-table-pagination-element-font-size, 14px);
  font-style: normal;
  font-weight: var(--otk-ui-table-pagination-element-font-weight, 500);
  line-height: normal;

  margin-right: ${(props) => {
  if (props.type === tablePaginationElementTypes.arrowLeft) {
    return "var(--otk-ui-table-pagination-element-arrow-left-margin-right, 16px)";
  }
}};

  margin-left: ${(props) => {
  if (props.type === tablePaginationElementTypes.arrowRight) {
    return "var(--otk-ui-table-pagination-element-arrow-right-margin-left, 16px)";
  }
}};

  color: ${(props) => {
  if (props.isSelected) {
    return "var(--otk-ui-table-pagination-element-is-selected-color, #fff)";
  }
  return "var(--otk-ui-table-pagination-element-color, #94a6b8)";
}};

  background: ${(props) => {
  if (props.isSelected) {
    return "var(--otk-ui-table-pagination-element-is-selected-background, #57b1e5)";
  }
}};

  cursor: ${(props) => {
  if (props.isDisabled && !props.isSelected && props.type !== "dots") {
    return "var(--otk-ui-table-pagination-element-disabled-cursor, not-allowed)";
  }
}};

  svg {
    path {
      fill: ${(props) => {
  if ((props.type === tablePaginationElementTypes.arrowLeft || props.type === tablePaginationElementTypes.arrowRight) && props.isDisabled) {
    return "var(--otk-ui-table-pagination-element-arrow-icon-fill, #e2e8ee)";
  }
}};
    }
  }

  :hover {
    fill: ${(props) => {
  if (props.type === tablePaginationElementTypes.arrowLeft || props.type === tablePaginationElementTypes.arrowRight) {
    return "var(--otk-ui-table-pagination-element-arrow-icon-hover-fill, #94a6b8)";
  }
}};

    color: ${(props) => {
  if (props.isDisabled && !props.isSelected && props.type !== "dots") {
    return "var(--otk-ui-table-pagination-element-hover-color, #57b1e5)";
  }
}};

    cursor: ${(props) => {
  if (!props.isDisabled && !props.isSelected && props.type !== "dots") {
    return "var(--otk-ui-table-pagination-element-hover-cursor, pointer)";
  }
  if ((props.type === tablePaginationElementTypes.arrowLeft || props.type === tablePaginationElementTypes.arrowRight) && props.isDisabled) {
    return "var(--otk-ui-table-pagination-element-hover-disabled-cursor, not-allowed)";
  }
}};
  }
`;

// src/components/table/table-pagination-arrow-icon-component.tsx
import React31 from "react";
import style from "styled-components";
import { ArrowLineLeftIcon, ArrowLineRightIcon } from "@ontruck/fe-icons";
var TablePaginationArrowLeftIconComponent = style(ArrowLineLeftIcon)`
  path {
    fill: var(--otk-ui-table-pagination-arrow-left-icon-fill, #94a6b8);
  }
`;
var TablePaginationArrowRightIconComponent = style(ArrowLineRightIcon)`
  path {
    fill: var(--otk-ui-table-pagination-arrow-right-icon-fill, #94a6b8);
  }
`;
var tablePaginationArrowDirections = {
  left: "left",
  right: "right"
};
function TablePaginationArrowIconComponent({
  direction = tablePaginationArrowDirections.left
}) {
  if (direction === tablePaginationArrowDirections.left) {
    return /* @__PURE__ */ React31.createElement(TablePaginationArrowLeftIconComponent, null);
  }
  return /* @__PURE__ */ React31.createElement(TablePaginationArrowRightIconComponent, null);
}

// src/components/table/table-cell-component.tsx
import styled47 from "styled-components";
var TableCellComponent = styled47.div`
  padding: ${({ padding }) => padding || "5px 16px"};
  display: ${({ display }) => display || "flex"};
  align-items: ${({ alignItems }) => alignItems || "center"};
  justify-content: ${({ justifyContent }) => justifyContent || "flex-start"};

  border-right: ${({ borderRight, border = true }) => {
  if (borderRight) {
    return borderRight;
  }
  if (border) {
    return "var(--otk-ui-table-cell-border-right, none)";
  }
  return "none";
}};

  border-left: ${({ borderLeft, border = true }) => {
  if (borderLeft) {
    return borderLeft;
  }
  if (border) {
    return "var(--otk-ui-table-cell-border-left, none)";
  }
  return "none";
}};

  margin-left: ${({ marginLeft }) => marginLeft || "0"};
  margin-right: ${({ marginRight }) => marginRight || "0"};
  width: ${({ width }) => width || "auto"};
  height: ${({ height }) => height || "100%"};
  background: ${({ background }) => background || "transparent"};
  flex: ${({ flex }) => flex || "unset"};
  flex-shrink: 0;
  box-sizing: border-box;
`;

// src/components/table/table-pagination-component.tsx
function TablePaginationComponent({
  resultsPerPageLabel,
  numberOfItems,
  onChangePageSize,
  onGoToNextPage,
  onGoToPage,
  onGoToPreviousPage,
  pageSizes,
  pageSize,
  pageIndex,
  width,
  justifyContent
}) {
  const numberOfPages = Math.ceil(numberOfItems / pageSize);
  const paginationElements = useMemo5(
    () => getTablePaginationElementsService(pageIndex, numberOfPages),
    [pageIndex, numberOfPages]
  );
  const pageSizesOptions = useMemo5(
    () => pageSizes ? pageSizes.map((s) => {
      return {
        label: s.toString(),
        value: s.toString()
      };
    }) : [],
    [pageSizes]
  );
  const handleGoToPreviousPage = () => {
    if (pageIndex > 1 && onGoToPreviousPage) {
      onGoToPreviousPage(pageIndex - 1);
    }
  };
  const handleGoToNextPage = () => {
    if (pageIndex < numberOfPages && onGoToNextPage) {
      onGoToNextPage(pageIndex + 1);
    }
  };
  const goToPageHandle = (page) => {
    if (onGoToPage) {
      onGoToPage(page);
    }
  };
  const handleChangePageSize = (_, value) => {
    if (onChangePageSize && value) {
      onChangePageSize(Number(value));
    }
  };
  return /* @__PURE__ */ React32.createElement(
    FlexComponent,
    {
      alignItems: "stretch",
      backgroundColor: "var(--otk-ui-table-pagination-background-color)",
      borderTop: "var(--otk-ui-table-pagination-border-top)",
      gap: "var(--otk-ui-table-pagination-gap)",
      height: "var(--otk-ui-table-pagination-height, 50px)",
      justifyContent,
      padding: "var(--otk-ui-table-pagination-padding, 4px 0)",
      width
    },
    pageSizes && pageSizes.length > 1 ? /* @__PURE__ */ React32.createElement(FlexComponent, { padding: "var(--otk-ui-table-pagination-page-sizer-padding, 4px 0)" }, /* @__PURE__ */ React32.createElement(
      TableCellComponent,
      {
        border: false,
        padding: "var(--otk-ui-table-pagination-page-sizer-cell-padding, 0 16px)"
      },
      /* @__PURE__ */ React32.createElement(FlexComponent, null, /* @__PURE__ */ React32.createElement(
        Input,
        {
          growTop: true,
          handleChangeValue: handleChangePageSize,
          label: resultsPerPageLabel,
          name: "pagination-size",
          options: pageSizesOptions,
          type: "select",
          value: pageSize.toString(),
          width: "var(--otk-ui-table-pagination-page-sizer-width, 140px)"
        }
      ))
    )) : null,
    paginationElements.length > 0 && pageSize > 0 && numberOfItems > 0 && numberOfPages > 0 ? /* @__PURE__ */ React32.createElement(
      FlexComponent,
      {
        margin: "var(--otk-ui-table-pagination-elements-margin, 0 0 0 auto)",
        padding: "var(--otk-ui-table-pagination-elements-padding, 4px 0)"
      },
      /* @__PURE__ */ React32.createElement(
        TableCellComponent,
        {
          border: false,
          padding: "var(--otk-ui-table-pagination-elements-cell-padding, 0 16px)"
        },
        paginationElements.map((element) => {
          let key;
          if (typeof element === "object") {
            key = element.id;
          } else if (typeof element === "number") {
            key = `page-${element}`;
          } else {
            key = `control-${element}`;
          }
          if (element === "<") {
            return /* @__PURE__ */ React32.createElement(
              TablePaginationElementComponent,
              {
                isDisabled: pageIndex === 1,
                key,
                onClick: handleGoToPreviousPage,
                type: "arrow-left"
              },
              /* @__PURE__ */ React32.createElement(TablePaginationArrowIconComponent, { direction: "left" })
            );
          } else if (element === ">") {
            return /* @__PURE__ */ React32.createElement(
              TablePaginationElementComponent,
              {
                isDisabled: pageIndex === numberOfPages,
                key,
                onClick: handleGoToNextPage,
                type: "arrow-right"
              },
              /* @__PURE__ */ React32.createElement(TablePaginationArrowIconComponent, { direction: "right" })
            );
          } else if (typeof element === "number") {
            return /* @__PURE__ */ React32.createElement(
              TablePaginationElementComponent,
              {
                isSelected: element === pageIndex,
                key,
                onClick: () => {
                  goToPageHandle(element);
                },
                type: "number"
              },
              element
            );
          }
          return /* @__PURE__ */ React32.createElement(TablePaginationElementComponent, { key, type: "dots" }, typeof element === "object" ? element.value : element);
        })
      )
    ) : null
  );
}

// src/components/table/table-header-component.tsx
import React33, { useMemo as useMemo6 } from "react";
var tableHeaderColumnAlignments = {
  left: "left",
  right: "right",
  center: "center"
};
var tableHeaderColumnGroups = {
  left: "left",
  right: "right",
  center: "center"
};
function TableHeaderComponent({
  columns
}) {
  const isColumns = useMemo6(() => columns && Object.keys(columns).length > 0, [columns]);
  const leftColumns = useMemo6(() => {
    if (!isColumns || !columns) {
      return [];
    }
    return Object.keys(columns).filter(
      (column) => columns[column].group === tableHeaderColumnGroups.left || columns[column].group === void 0
    ).map((column) => columns[column]);
  }, [columns, isColumns]);
  const rightColumns = useMemo6(() => {
    if (!isColumns || !columns) {
      return [];
    }
    return Object.keys(columns).filter((column) => columns[column].group === tableHeaderColumnGroups.right).map((column) => columns[column]);
  }, [columns, isColumns]);
  const renderColumns = (groupedColumns) => {
    return groupedColumns.map((column) => {
      const { cellComponent, id, label, alignment, width } = column;
      const padding = label ? void 0 : "0";
      if (cellComponent) {
        return /* @__PURE__ */ React33.createElement(
          TableCellComponent,
          {
            borderLeft: "none",
            borderRight: "var(--otk-ui-table-header-cell-border-right)",
            justifyContent: alignment,
            key: id,
            padding,
            width
          },
          cellComponent
        );
      }
      return /* @__PURE__ */ React33.createElement(
        TableCellComponent,
        {
          borderLeft: "none",
          borderRight: "var(--otk-ui-table-header-cell-border-right)",
          justifyContent: alignment,
          key: id,
          padding,
          width
        },
        label ? /* @__PURE__ */ React33.createElement(
          TextComponent,
          {
            color: "var(--otk-ui-table-header-cell-text-color, #94A6B8)",
            fontSize: "var(--otk-ui-table-header-cell-text-font-size, 12px)",
            fontWeight: "var(--otk-ui-table-header-cell-text-font-weight, 500)"
          },
          label
        ) : null
      );
    });
  };
  if (!isColumns) {
    return null;
  }
  return /* @__PURE__ */ React33.createElement(
    FlexComponent,
    {
      alignItems: "center",
      backgroundColor: "var(--otk-ui-table-header-background, #f2f5f8)",
      justifyContent: "space-between",
      padding: "var(--otk-ui-table-header-padding, 4px 0 4px 0)"
    },
    leftColumns.length > 0 ? /* @__PURE__ */ React33.createElement(FlexComponent, { alignItems: "center" }, renderColumns(leftColumns)) : null,
    rightColumns.length > 0 ? /* @__PURE__ */ React33.createElement(FlexComponent, { alignItems: "stretch", margin: "auto 0 0 0" }, renderColumns(rightColumns)) : null
  );
}

// src/components/table/table-summary-component.tsx
import React34 from "react";
function TableSummaryComponent({
  summaryComponent
}) {
  return /* @__PURE__ */ React34.createElement(
    FlexComponent,
    {
      alignItems: "center",
      backgroundColor: "var(--otk-ui-table-summary-background, #FFF)",
      borderBottom: "var(--otk-ui-table-summary-border-bottom, 1px solid #E2E8EE)",
      height: "var(--otk-ui-table-summary-height, 40px)"
    },
    summaryComponent
  );
}

// src/components/table/table-sticky-component.tsx
import styled48 from "styled-components";
var TableStickyComponent = styled48.header`
  width: 100%;
  display: flex;
  position: sticky;
  top: 0px;
  z-index: 99;
  flex-direction: column;
`;

// src/components/table/table-empty-state-component.tsx
import React35 from "react";
import styled49 from "styled-components";
var EmptyListComponent = styled49.div`
  display: flex;
  flex-grow: 1;
  padding: 60px 0;
  align-items: center;
  flex-direction: column;
  gap: 16px;
`;
function TableEmptyStateComponent({
  icon,
  message
}) {
  return /* @__PURE__ */ React35.createElement(FlexComponent, null, /* @__PURE__ */ React35.createElement(EmptyListComponent, null, icon, /* @__PURE__ */ React35.createElement(
    TextComponent,
    {
      color: "var(--otk-ui-table-pagination-empty-state-message-color,  #94A6B8)",
      fontWeight: "var(--otk-ui-table-pagination-empty-state-message-font-weight, 500)"
    },
    message
  )));
}

// src/components/table/table-rows-skeleton-component.tsx
import React36, { useMemo as useMemo7 } from "react";
import styled50 from "styled-components";
import ContentLoader from "react-content-loader";
var TableRowsSkeletonContainerComponent = styled50.section`
  width: 100%;
`;
function TableRowsSkeletonComponent({
  numberOfRows
}) {
  const lineHeight = 43;
  const marginTop = 5;
  const lineSeparation = 52;
  const checkBoxWidth = 35;
  const maxWidth = 1850;
  const uniqueKeys = useMemo7(() => {
    return Array.from({ length: numberOfRows }, (_, i) => `react-table-row-${i}`);
  }, [numberOfRows]);
  return /* @__PURE__ */ React36.createElement(TableRowsSkeletonContainerComponent, null, /* @__PURE__ */ React36.createElement(
    ContentLoader,
    {
      backgroundColor: "#f3f3f3",
      foregroundColor: "#ecebeb",
      height: "100%",
      speed: 2,
      viewBox: "0 0 1920 1080",
      width: "100%"
    },
    uniqueKeys.map((key, i) => /* @__PURE__ */ React36.createElement(React36.Fragment, { key }, /* @__PURE__ */ React36.createElement(
      "rect",
      {
        height: `${lineHeight}`,
        rx: "3",
        ry: "3",
        width: `${checkBoxWidth}`,
        x: "5",
        y: marginTop + i * lineSeparation
      }
    ), /* @__PURE__ */ React36.createElement(
      "rect",
      {
        height: `${lineHeight}`,
        rx: "3",
        ry: "3",
        width: `${maxWidth}`,
        x: "45",
        y: marginTop + i * lineSeparation
      }
    )))
  ));
}

// src/components/table/table-error-component.tsx
import React37 from "react";
function TableErrorComponent({ errorLabel }) {
  return /* @__PURE__ */ React37.createElement(FlexComponent, { alignItems: "stretch", flexGrow: "1", justifyContent: "center" }, /* @__PURE__ */ React37.createElement(
    FlexComponent,
    {
      alignItems: "center",
      flexDirection: "column",
      flexGrow: "1",
      padding: "var(--otk-ui-table-error-padding, 60px 0 0 0)"
    },
    /* @__PURE__ */ React37.createElement(
      TextComponent,
      {
        color: "var(--otk-ui-table-error-label-color, #cc000a)",
        fontSize: "var(--otk-ui-table-error-label-font-size, 14px)",
        fontWeight: "var(--otk-ui-table-error-label-font-weight, 500)"
      },
      errorLabel ? errorLabel : "There was an error searching for items"
    )
  ));
}

// src/components/table/table-rows-component.tsx
import React42, { useCallback as useCallback3 } from "react";
import styled52 from "styled-components";

// src/components/table/table-row-component.tsx
import React38 from "react";
function TableRowComponent({
  children,
  hasIconCell = false,
  height,
  iconCellWidth,
  isLastRow = false
}) {
  return /* @__PURE__ */ React38.createElement(
    FlexComponent,
    {
      alignItems: "center",
      backgroundColor: "var(--otk-ui-table-row-background-color, transparent)",
      borderBottom: isLastRow ? "var(--otk-ui-table-row-border-bottom, 1px solid #E2E8EE)" : "none",
      borderTop: "var(--otk-ui-table-row-border-top, 1px solid #E2E8EE)",
      height: height ? height : "var(--otk-ui-table-row-height, 40px)"
    },
    hasIconCell ? /* @__PURE__ */ React38.createElement(
      TableCellComponent,
      {
        background: "var(--otk-ui-table-row-cell-icon-background-color, #E2E8EE);",
        borderRight: "--otk-ui-table-row-cell-icon-border-right, none);",
        justifyContent: "center",
        padding: "unset",
        width: iconCellWidth
      }
    ) : null,
    children
  );
}

// src/components/table/table-row-accordion-component.tsx
import React41, { useCallback as useCallback2, useMemo as useMemo8, useState as useState16 } from "react";
import styled51 from "styled-components";

// src/components/table/table-row-accordion-parent-component.tsx
import React39 from "react";
function TableRowAccordionParentComponent({
  children,
  hasIconCell = true,
  iconCellWidth,
  iconComponent,
  isOpen = false,
  isLastRow = false,
  minHeight
}) {
  return /* @__PURE__ */ React39.createElement(
    FlexComponent,
    {
      alignItems: "center",
      backgroundColor: "var(--otk-ui-table-row-accordion-parent-background-color, transparent)",
      borderBottom: isOpen || isLastRow ? "var(--otk-ui-table-row-accordion-parent-border-bottom, 1px solid #e2e8ee)" : "none",
      borderTop: "var(--otk-ui-table-row-accordion-parent-border-top, 1px solid #e2e8ee)",
      minHeight: minHeight ? minHeight : "var(--otk-ui-table-row-accordion-parent-min-height, 45px)"
    },
    hasIconCell ? /* @__PURE__ */ React39.createElement(
      TableCellComponent,
      {
        alignItems: "center",
        background: "var(--otk-ui-table-row-accordion-parent-cell-icon-background-color, #E2E8EE);",
        borderLeft: "none",
        borderRight: "var(--otk-ui-table-row-accordion-parent-cell-icon-border-right, 1px solid #CCD7E0);",
        justifyContent: "center",
        padding: "0",
        width: iconCellWidth
      },
      iconComponent ? iconComponent : /* @__PURE__ */ React39.createElement(AccordionIconComponent, { open: isOpen })
    ) : null,
    children
  );
}

// src/components/table/table-row-accordion-child-component.tsx
import React40 from "react";
function TableRowAccordionChildComponent({
  hasIconCell = true,
  iconCellWidth,
  isLastRow = false,
  height,
  children
}) {
  return /* @__PURE__ */ React40.createElement(
    FlexComponent,
    {
      alignItems: "center",
      backgroundColor: "var(--otk-ui-table-row-accordion-child-background-color, transparent)",
      borderBottom: isLastRow ? "var(--otk-ui-table-row-accordion-child-border-bottom, 1px solid #E2E8EE)" : "none",
      borderTop: "var(--otk-ui-table-row-accordion-child-border-top, 1px solid #E2E8EE)",
      height: height ? height : "var(--otk-ui-table-row-accordion-child-height, 40px)"
    },
    hasIconCell ? /* @__PURE__ */ React40.createElement(
      TableCellComponent,
      {
        background: "var(--otk-ui-table-row-accordion-child-cell-icon-background-color, #E2E8EE);",
        borderLeft: "none",
        borderRight: "var(--otk-ui-table-row-accordion-child-cell-icon-border-right, 1px solid #CCD7E0);",
        justifyContent: "center",
        padding: "unset",
        width: iconCellWidth
      }
    ) : null,
    children
  );
}

// src/components/table/table-row-accordion-component.tsx
var TableRowAccordionWrapperComponent = styled51(AccordionComponent)`
  height: auto;
  border-left: none;
  border-right: none;
  border: none;

  header {
    display: flex;
    padding: unset;
    border-top: none;
    border-bottom: none;

    > div {
      flex: 1;
    }

    :hover {
      cursor: default;
    }
  }
`;
var TableRowAccordionParentTriggerComponent = styled51(AccordionTriggerComponent)`
  padding: unset;
  border-top: 3px solid var(--grey-200, #ccd7e0);
  border-bottom: none;
`;
var TableRowAccordionContentWrapperComponent = styled51.div`
  border-top: ${({ open }) => open ? "var(--otk-ui-table-row-accordion-content-border-top, 3px solid #ccd7e0)" : "none"};
  border-bottom: ${({ open }) => open ? "var(--otk-ui-table-row-accordion-content-border-bottom, 3px solid #ccd7e0)" : "none"};
  height: ${({ open }) => open ? "auto" : "0"};
`;
var TableRowAccordionContentComponent = styled51(AccordionContentComponent)`
  padding: unset;
  border-top: none;
  border-bottom: none;

  ${TableCellComponent} {
    display: ${({ open }) => open ? "flex" : "none"};
  }
`;
function TableRowAccordionComponent({
  iconCellWidth,
  parentRowMinHeight,
  rowsCells,
  isLastRow = false
}) {
  const [isOpen, setIsOpen] = useState16(rowsCells.isOpen || false);
  const parentCellNumberOpensAccordion = rowsCells.isOpen !== void 0 && rowsCells.isOpen;
  const getParentRowCellsComponents = useCallback2(() => {
    var _a;
    if ((_a = rowsCells.parent.row) == null ? void 0 : _a.cells) {
      return /* @__PURE__ */ React41.createElement(
        TableRowAccordionParentComponent,
        {
          iconCellWidth,
          isLastRow,
          isOpen,
          minHeight: parentRowMinHeight
        },
        rowsCells.parent.row.cells.length > 0 ? rowsCells.parent.row.cells.map((c, i) => {
          var _a2;
          const numberOfCells = (_a2 = rowsCells.parent.row) == null ? void 0 : _a2.cells.length;
          const isFirstCell = i === 0;
          const isLastCell = numberOfCells ? i === numberOfCells - 1 : false;
          return /* @__PURE__ */ React41.createElement(
            TableCellComponent,
            {
              alignItems: c.alignItems,
              background: c.backgroundColor,
              border: c.border,
              borderLeft: isFirstCell ? "none" : c.borderLeft,
              borderRight: isLastCell ? "none" : c.borderRight,
              display: c.display,
              flex: c.flex,
              height: c.height,
              justifyContent: c.justifyContent,
              key: c.id,
              marginLeft: c.marginLeft,
              padding: c.padding,
              width: c.width
            },
            c.content
          );
        }) : null
      );
    }
    return null;
  }, [iconCellWidth, isOpen, parentRowMinHeight, rowsCells.parent.row, isLastRow]);
  const getChildRowCellsComponents = useCallback2(
    (row) => {
      return /* @__PURE__ */ React41.createElement(TableRowAccordionChildComponent, { iconCellWidth }, row.cells.map((c) => {
        const isFirstCell = row.cells.indexOf(c) === 0;
        const isLastCell = row.cells.indexOf(c) === row.cells.length - 1;
        return /* @__PURE__ */ React41.createElement(
          TableCellComponent,
          {
            alignItems: c.alignItems,
            background: c.backgroundColor,
            border: c.border,
            borderLeft: isFirstCell ? "none" : c.borderLeft,
            borderRight: isLastCell ? "none" : c.borderRight,
            flex: c.flex,
            height: c.height,
            justifyContent: c.justifyContent,
            key: c.id,
            marginLeft: c.marginLeft,
            padding: c.padding,
            width: c.width
          },
          c.content
        );
      }));
    },
    [iconCellWidth]
  );
  const getChildrenRowsCellsComponents = useCallback2(() => {
    if (rowsCells.children.length > 0) {
      return rowsCells.children.map((child) => {
        if (child.row) {
          return getChildRowCellsComponents(child.row);
        }
        return null;
      }).filter((c) => c !== null);
    }
    return null;
  }, [getChildRowCellsComponents, rowsCells.children]);
  const handleToggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  const parentComponent = useMemo8(
    () => getParentRowCellsComponents(),
    [getParentRowCellsComponents]
  );
  const childrenComponents = useMemo8(
    () => getChildrenRowsCellsComponents(),
    [getChildrenRowsCellsComponents]
  );
  return /* @__PURE__ */ React41.createElement(TableRowAccordionWrapperComponent, null, /* @__PURE__ */ React41.createElement(
    TableRowAccordionParentTriggerComponent,
    {
      onClick: !parentCellNumberOpensAccordion ? handleToggleAccordion : void 0,
      open: isOpen
    },
    parentComponent
  ), /* @__PURE__ */ React41.createElement(TableRowAccordionContentWrapperComponent, { open: isOpen }, /* @__PURE__ */ React41.createElement(TableRowAccordionContentComponent, { open: isOpen }, childrenComponents)));
}

// src/components/table/get-is-table-row-cells-accordion-service.ts
function getIsTableRowCellsAccordionService(row) {
  if (typeof row === "object") {
    return "parent" in row && "children" in row;
  }
  return false;
}

// src/components/table/get-is-table-row-cells-simple-service.ts
import { isValidElement } from "react";
function getIsTableRowCellsSimpleService(rowCells) {
  if (!rowCells) {
    return false;
  }
  return "cells" in rowCells && Array.isArray(rowCells.cells) && rowCells.cells.every((cell) => isValidElement(cell.content) || typeof cell.content === "string");
}

// src/components/table/get-has-table-row-cells-accordion-service.ts
function getHasTableRowCellsAccordionService(rowsCells) {
  if (!rowsCells) {
    return false;
  }
  return rowsCells.some((r) => getIsTableRowCellsAccordionService(r));
}

// src/components/table/get-table-row-accordion-icon-cell-width-service.ts
function getTableRowAccordionIconCellWidthService(rowCells) {
  var _a;
  const isTableRowCellsAccordion = getIsTableRowCellsAccordionService(rowCells);
  if (!isTableRowCellsAccordion) {
    return;
  }
  return (_a = rowCells.parent.accordionIconCell) == null ? void 0 : _a.width;
}

// src/components/table/table-rows-component.tsx
var ACCORDION_ICON_CELL_WIDTH = "16px";
var TableRowsContainerComponent = styled52.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
function TableRowsComponent({
  rowsCells,
  rowsComponents
}) {
  const getTableRowsComponents = useCallback3(() => {
    if (rowsComponents) {
      return rowsComponents.map((r) => r);
    }
    return null;
  }, [rowsComponents]);
  const getTableRowCellsSimpleComponents = useCallback3(
    (simpleRowCells, isLastRow) => {
      const hasTableRowCellsAccordion = getHasTableRowCellsAccordionService(rowsCells);
      if (simpleRowCells.cells.length > 0) {
        let iconCellWidth;
        if (hasTableRowCellsAccordion) {
          iconCellWidth = getTableRowAccordionIconCellWidthService(simpleRowCells);
        }
        return /* @__PURE__ */ React42.createElement(
          TableRowComponent,
          {
            hasIconCell: hasTableRowCellsAccordion,
            height: simpleRowCells.height,
            iconCellWidth: iconCellWidth || ACCORDION_ICON_CELL_WIDTH,
            isLastRow,
            key: simpleRowCells.id
          },
          simpleRowCells.cells.map((c, i) => {
            const isLastCell = simpleRowCells.cells.length ? i === simpleRowCells.cells.length - 1 : false;
            const isFirstCell = i === 0;
            return /* @__PURE__ */ React42.createElement(
              TableCellComponent,
              {
                alignItems: c.alignItems,
                background: c.backgroundColor,
                border: c.border,
                borderLeft: isFirstCell ? "none" : c.borderLeft,
                borderRight: isLastCell ? "none" : c.borderRight,
                flex: c.flex,
                height: c.height,
                justifyContent: c.justifyContent,
                key: c.id,
                marginLeft: c.marginLeft,
                padding: c.padding,
                width: c.width
              },
              c.content
            );
          })
        );
      }
      return null;
    },
    [rowsCells]
  );
  const getTableRowCellsAccordionComponents = useCallback3(
    (rowCells, isLastRow) => {
      var _a;
      return /* @__PURE__ */ React42.createElement(
        TableRowAccordionComponent,
        {
          iconCellWidth: ((_a = rowCells.parent.accordionIconCell) == null ? void 0 : _a.width) || ACCORDION_ICON_CELL_WIDTH,
          isLastRow,
          key: rowCells.id,
          parentRowMinHeight: rowCells.parent.minHeight,
          rowsCells: rowCells
        }
      );
    },
    []
  );
  const getTableRowsCellsComponents = useCallback3(() => {
    if (rowsCells && rowsCells.length > 0) {
      return rowsCells.map((rowCells, i) => {
        const isTableRowCellsSimple = getIsTableRowCellsSimpleService(rowCells);
        const isLastRow = rowsCells.length ? i === rowsCells.length - 1 : false;
        if (isTableRowCellsSimple) {
          return getTableRowCellsSimpleComponents(rowCells, isLastRow);
        }
        const isTableRowCellsAccordionComponent = getIsTableRowCellsAccordionService(rowCells);
        if (isTableRowCellsAccordionComponent) {
          return getTableRowCellsAccordionComponents(rowCells, isLastRow);
        }
        return null;
      }).filter((r) => r !== null);
    }
    return null;
  }, [getTableRowCellsSimpleComponents, getTableRowCellsAccordionComponents, rowsCells]);
  const getComponents = useCallback3(() => {
    if (rowsComponents) {
      return getTableRowsComponents();
    }
    if (rowsCells) {
      return getTableRowsCellsComponents();
    }
    return null;
  }, [getTableRowsComponents, getTableRowsCellsComponents, rowsCells, rowsComponents]);
  const components = getComponents();
  return /* @__PURE__ */ React42.createElement(TableRowsContainerComponent, null, components);
}

// src/components/table/table-scrollable-container-component.tsx
import styled53 from "styled-components";
var TableScrollableContainerComponent = styled53.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: start;
  position: relative;
  background-color: transparent;
  overflow: auto;
  width: 100%;
`;

// src/components/table/table-responsive-containers.tsx
import styled54 from "styled-components";
var TableResponsiveContainerComponent = styled54.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: auto; /* Enable horizontal scroll for entire table */
  overflow-y: hidden;
`;
var TableResponsiveHeaderContainerComponent = styled54.div`
  position: sticky;
  top: 0;
  min-width: fit-content; /* Ensure it takes full width of content */
  width: 100%;
  z-index: 100;
`;
var TableResponsiveFooterContainerComponent = styled54.div`
  position: sticky;
  bottom: 0;
  min-width: fit-content; /* Ensure it takes full width of content */
  width: 100%;
  z-index: 100;
`;
var TableResponsiveBodyContainerComponent = styled54.div`
  flex: 1;
  min-width: fit-content; /* Ensure it takes full width of content */
  width: 100%;
  overflow-y: auto; /* Only vertical scroll in body */
  overflow-x: hidden;
  min-height: 0;
`;

// src/components/table/table-component.tsx
var TableContainerComponent = styled55.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`;
function TableComponent({
  columns,
  emptyStateIcon = /* @__PURE__ */ React43.createElement(EmptyListSearchIcon, null),
  emptyStateMessage = "No items found",
  errorComponent,
  isError = false,
  isLoading = false,
  numberOfItems,
  onChangePageSize,
  onGoToNextPage,
  onGoToPage,
  onGoToPreviousPage,
  pageIndex,
  pageSize,
  pageSizes,
  resultsPerPageLabel = "Results per page",
  rowsCells,
  rowsComponents,
  scrollCacheKey,
  stickyFooterComponent,
  summaryComponent,
  isResponsive = false
}) {
  const { manuallyScrollToElementRef, saveCurrentPositionToCache, scrollToSavedCache } = useScrollCacheHook(`table-${scrollCacheKey}`);
  useEffect11(() => {
    scrollToSavedCache();
  }, [scrollToSavedCache]);
  return /* @__PURE__ */ React43.createElement(TableContainerComponent, null, /* @__PURE__ */ React43.createElement(ConditionalWrapper, { condition: isResponsive, wrapper: TableResponsiveContainerComponent }, columns || summaryComponent ? /* @__PURE__ */ React43.createElement(
    ConditionalWrapper,
    {
      condition: isResponsive,
      wrapper: TableResponsiveHeaderContainerComponent
    },
    /* @__PURE__ */ React43.createElement(TableStickyComponent, null, columns ? /* @__PURE__ */ React43.createElement(TableHeaderComponent, { columns }) : null, summaryComponent ? /* @__PURE__ */ React43.createElement(TableSummaryComponent, { summaryComponent }) : null)
  ) : null, /* @__PURE__ */ React43.createElement(
    ConditionalWrapper,
    {
      condition: isResponsive,
      wrapper: TableResponsiveBodyContainerComponent
    },
    /* @__PURE__ */ React43.createElement(
      TableScrollableContainerComponent,
      {
        onScroll: saveCurrentPositionToCache,
        ref: manuallyScrollToElementRef
      },
      (() => {
        if (isError) {
          return errorComponent ? errorComponent : /* @__PURE__ */ React43.createElement(TableErrorComponent, null);
        }
        if (isLoading) {
          return /* @__PURE__ */ React43.createElement(TableRowsSkeletonComponent, { numberOfRows: pageSize || numberOfItems });
        }
        if (numberOfItems === 0 || (rowsComponents == null ? void 0 : rowsComponents.length) === 0 && (rowsCells == null ? void 0 : rowsCells.length) === 0) {
          return /* @__PURE__ */ React43.createElement(TableEmptyStateComponent, { icon: emptyStateIcon, message: emptyStateMessage });
        }
        return /* @__PURE__ */ React43.createElement(TableRowsComponent, { rowsCells, rowsComponents });
      })()
    )
  ), (() => {
    if (stickyFooterComponent) {
      return /* @__PURE__ */ React43.createElement(
        ConditionalWrapper,
        {
          condition: isResponsive,
          wrapper: TableResponsiveFooterContainerComponent
        },
        /* @__PURE__ */ React43.createElement(TableStickyComponent, null, stickyFooterComponent)
      );
    }
    if (!pageIndex || !pageSize) {
      return null;
    }
    return /* @__PURE__ */ React43.createElement(
      ConditionalWrapper,
      {
        condition: isResponsive,
        wrapper: TableResponsiveFooterContainerComponent
      },
      /* @__PURE__ */ React43.createElement(TableStickyComponent, null, /* @__PURE__ */ React43.createElement(
        TablePaginationComponent,
        {
          numberOfItems,
          onChangePageSize,
          onGoToNextPage,
          onGoToPage,
          onGoToPreviousPage,
          pageIndex,
          pageSize,
          pageSizes,
          resultsPerPageLabel
        }
      ))
    );
  })()));
}

// src/components/error-message.tsx
import styled56 from "styled-components";
var ErrorMessage = styled56.div`
  width: 100%;
  padding: 16px;
  background-color: var(--error-100);
  color: var(--error-700);
  border: 1px solid var(--error-700);
  border-radius: 4px;
  text-align: center;
`;

// src/components/drawer/drawer-component.tsx
import React44, { useLayoutEffect as useLayoutEffect3, useState as useState17 } from "react";
import { createPortal as createPortal3 } from "react-dom";
import styled57 from "styled-components";
import { IconComponent as IconComponent3 } from "@ontruck/fe-icons";
var positions = {
  top: "top",
  right: "right",
  bottom: "bottom",
  left: "left"
};
var nodeRegionsIds = {
  right: DRAWER_REGION_ID_RIGHT,
  left: DRAWER_REGION_ID_LEFT
};
var transforms = {
  [positions.top]: "translateY(-100%)",
  [positions.right]: "translateX(100%)",
  [positions.bottom]: "translateY(100%)",
  [positions.left]: "translateX(-100%)"
};
var placements = {
  [positions.top]: {
    top: 0,
    right: 0,
    left: 0
  },
  [positions.right]: {
    top: 0,
    right: 0,
    bottom: 0
  },
  [positions.bottom]: {
    right: 0,
    bottom: 0,
    left: 0
  },
  [positions.left]: {
    top: 0,
    bottom: 0,
    left: 0
  }
};
var DrawerWrapper = styled57.div`
  display: block;
  width: ${({ position, size }) => position !== positions.top && position !== positions.bottom && size ? size : "300px"};
  height: ${({ position, size }) => (position === positions.top || position === positions.bottom) && size ? size : "100%"};
`;
var DrawerOverlay = styled57.div`
  position: fixed;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  z-index: 99999;
  display: ${({ open }) => open ? null : "none"};
  background-color: var(--otk-theme-color-black);
  opacity: ${({ opacity }) => opacity ? opacity : "0"};
`;
var DrawerContent = styled57.div`
  display: block;
  box-sizing: border-box;
  position: absolute;
  ${({ position }) => placements[position]}
  z-index: 99999;
  width: ${({ position, size, open }) => position !== positions.top && position !== positions.bottom && open ? size : "300px"};
  height: ${({ position, size }) => position === positions.right || position === positions.left ? "100%" : size};
  transform: ${({ open, position }) => !open ? transforms[position] : null};
  transition: transform 0.2s ease-out;
  overflow-x: hidden;
  overflow-y: ${({ scrollable }) => scrollable ? "auto" : "hidden"};
  color: var(--otk-theme-color-black);
  background-color: ${({ backgroundColor }) => backgroundColor || "var(--otk-theme-color-white)"};
  box-shadow: -10px 0px 10px rgba(0, 0, 0, 0.19);
`;
var DrawerHeader = styled57.header`
  position: relative;
  width: 100%;
`;
var DrawerCloseButton = styled57(ButtonComponent)`
  position: absolute;
  right: 15px;
  top: 15px;
`;
function DrawerComponent({
  backgroundColor = "var(--otk-theme-color-white)",
  children,
  headerComponent,
  isOpen = false,
  nodeRegionId = nodeRegionsIds.right,
  onClose,
  opacity = 0,
  position = positions.right,
  positionAuto = true,
  scrollable = true,
  size = "40%",
  withHeader = true
}) {
  const [nodeRegion, setNodeRegion] = useState17();
  useKeyPressHook({
    key: "Escape",
    onKeyPressed: () => {
      if (onClose) {
        onClose();
      }
    }
  });
  useLayoutEffect3(() => {
    if (nodeRegion || !positionAuto) {
      return;
    }
    const node = document.getElementById(nodeRegionId);
    if (!node) {
      console.error(`Node with id ${nodeRegionId} not found`);
      return;
    }
    setNodeRegion(node);
  }, [nodeRegion, setNodeRegion, positionAuto, nodeRegionId]);
  const renderDrawerHeader = () => {
    if (headerComponent) {
      return headerComponent;
    }
    if (withHeader) {
      return /* @__PURE__ */ React44.createElement(DrawerHeader, null, /* @__PURE__ */ React44.createElement(DrawerCloseButton, { name: "close", onClick: onClose, variant: "ghost" }, /* @__PURE__ */ React44.createElement(IconComponent3, { colorVariant: "primary", name: "close" })));
    }
    return null;
  };
  const renderDrawer = () => {
    return /* @__PURE__ */ React44.createElement(DrawerWrapper, { open: isOpen, position, size }, /* @__PURE__ */ React44.createElement(DrawerOverlay, { onClick: onClose, opacity, open: isOpen }), /* @__PURE__ */ React44.createElement(
      DrawerContent,
      {
        backgroundColor,
        open: isOpen,
        position,
        scrollable,
        size
      },
      /* @__PURE__ */ React44.createElement(
        FlexComponent,
        {
          alignItems: "start",
          flexDirection: "column",
          height: "100%",
          justifyContent: "start"
        },
        renderDrawerHeader(),
        children
      )
    ));
  };
  return /* @__PURE__ */ React44.createElement(React44.Fragment, null, nodeRegion ? createPortal3(renderDrawer(), nodeRegion) : renderDrawer());
}
DrawerComponent.Header = DrawerHeader;

// src/components/form-container.tsx
import styled58 from "styled-components";
var FormContainer = styled58.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  form {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    position: relative;
  }
`;

// src/components/half.tsx
import styled59 from "styled-components";
var Half = styled59.div`
  flex: 0.5 0 0;
`;

// src/components/page/page-content.tsx
import React45 from "react";
import styled60 from "styled-components";
var PageContentRootComponent = styled60.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: auto;
  ${({ withBackground }) => withBackground && `background-color: var(--grey100);`}
`;
function PageContent(_a) {
  var _b = _a, {
    children,
    className,
    withBackground
  } = _b, props = __objRest(_b, [
    "children",
    "className",
    "withBackground"
  ]);
  return /* @__PURE__ */ React45.createElement(
    PageContentRootComponent,
    __spreadValues({
      className: `page-content-component ${className}`,
      withBackground
    }, props),
    children
  );
}

// src/components/page/page-layout.tsx
import styled61 from "styled-components";
var PageLayout = styled61.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// src/components/section/section-body.tsx
import styled62 from "styled-components";
var SectionBody = styled62.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

// src/components/section/section-container.tsx
import styled63 from "styled-components";
var SectionContainer = styled63.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

// src/components/section/section-line.tsx
import styled64 from "styled-components";
var SectionLine = styled64.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

// src/components/section/section-title.tsx
import styled65 from "styled-components";
var SectionTitle = styled65.div`
  color: var(--grey-900);
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

// src/components/sticky/sticky.tsx
import styled66 from "styled-components";
var Sticky = styled66.div`
  display: flex;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  background-color: white;
  border-top: 1px solid var(--grey300);
  bottom: 0;
  top: unset;
  height: 64px;
  display: flex;
  padding: 16px 24px;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
`;

// src/components/tooltip/tooltip-componnet.tsx
import React46, { useRef as useRef12, useState as useState18 } from "react";
import styled67 from "styled-components";
import {
  offset,
  shift,
  flip,
  useFloating,
  autoUpdate,
  useInteractions,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  arrow,
  FloatingArrow,
  useTransitionStyles
} from "@floating-ui/react";
var TooltipHeaderComponent = styled67.div`
  color: var(--primary-background);
  font-size: 14px;
  font-weight: bold;
`;
var TooltipBodyComponent = styled67.div`
  color: var(--primary-background);
  font-size: 12px;
`;
var TooltipUnorderedListComponent = styled67.ul`
  margin: 0;
  margin-left: -20px;
`;
var TooltipContainerComponent = styled67.div`
  border-radius: 0.8rem;
  padding: 1.6rem;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.5);
  color: #fff;
  background: #182a33;
  border: none;
  font-size: 1.2rem;
  font-weight: normal;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
var TooltipArrowComponent = styled67(FloatingArrow)`
  fill: #182a33;
`;
function TooltipComponent({
  content,
  renderOpener,
  placement,
  isDisabled = false
}) {
  const [isOpen, setIsOpen] = useState18(false);
  const arrowRef = useRef12(null);
  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(12),
      flip(),
      shift(),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  });
  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  const { styles: transitionStyles } = useTransitionStyles(context, {});
  return /* @__PURE__ */ React46.createElement(React46.Fragment, null, renderOpener(__spreadValues({ ref: setReference }, getReferenceProps())), isOpen && !isDisabled && content ? /* @__PURE__ */ React46.createElement("div", __spreadValues({ ref: setFloating, style: __spreadProps(__spreadValues({}, floatingStyles), { zIndex: 1e3 }) }, getFloatingProps()), /* @__PURE__ */ React46.createElement(TooltipContainerComponent, { style: transitionStyles }, /* @__PURE__ */ React46.createElement(TooltipArrowComponent, { context, height: 8, ref: arrowRef, tipRadius: 2 }), content)) : null);
}

// src/components/popover/popover.tsx
import React47, { useRef as useRef13, useState as useState19, useEffect as useEffect12 } from "react";
import {
  offset as offset2,
  shift as shift2,
  flip as flip2,
  useFloating as useFloating2,
  autoUpdate as autoUpdate2,
  arrow as arrow2,
  FloatingArrow as FloatingArrow2
} from "@floating-ui/react";
import styled68 from "styled-components";
var PopoverArrow = styled68(FloatingArrow2)`
  fill: var(--white);
  width: 30px;
  height: 30px;
  margin-left: -2px;
`;
function Popover({
  className,
  content,
  renderOpener,
  placement,
  isDisabled = false
}) {
  const [isOpen, setIsOpen] = useState19(false);
  const arrowRef = useRef13(null);
  const popoverRef = useRef13(null);
  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context
  } = useFloating2({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset2(12),
      flip2(),
      shift2(),
      arrow2({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate2
  });
  const handleOpenerClick = () => {
    setIsOpen((prev) => !prev);
  };
  useEffect12(() => {
    function handleClickOutsideOrScroll(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutsideOrScroll);
      document.addEventListener("scroll", handleClickOutsideOrScroll, true);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideOrScroll);
      document.removeEventListener("scroll", handleClickOutsideOrScroll, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideOrScroll);
      document.removeEventListener("scroll", handleClickOutsideOrScroll, true);
    };
  }, [isOpen]);
  return /* @__PURE__ */ React47.createElement(React47.Fragment, null, renderOpener({ ref: setReference, onClick: handleOpenerClick }), " ", isOpen && !isDisabled && content ? (
    // the popover content is unstyled
    // we style it where we use it
    // this way we can have different styles for different popovers
    /* @__PURE__ */ React47.createElement(
      "div",
      {
        className,
        ref: (node) => {
          setFloating(node);
          popoverRef.current = node;
        },
        style: __spreadProps(__spreadValues({}, floatingStyles), { zIndex: 1e3 })
      },
      /* @__PURE__ */ React47.createElement("div", null, /* @__PURE__ */ React47.createElement(PopoverArrow, { context, height: 8, ref: arrowRef, tipRadius: 2 }), content)
    )
  ) : null);
}

// src/components/individual-actions/individual-actions-menu-button-component.tsx
import React48 from "react";
import styled69 from "styled-components";
var IndividualActionsMenuButtonContainerComponent = styled69.div`
  width: 24px;
  height: 100%;
`;
var IndividualActionsMenuButtonStyledComponent = styled69.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 100%;
  width: 100%;
  cursor: pointer;
  background: var(--grey-200, #e2e8ee);
  border: none;
  &:hover {
    background: var(--grey-300, #d4d4d4);
  }
`;
function IndividualActionsMenuButtonComponent({
  ref,
  onClick
}) {
  return /* @__PURE__ */ React48.createElement(IndividualActionsMenuButtonContainerComponent, null, /* @__PURE__ */ React48.createElement(IndividualActionsMenuButtonStyledComponent, { onClick, ref }, /* @__PURE__ */ React48.createElement(
    "svg",
    {
      fill: "none",
      height: "19",
      viewBox: "0 0 18 19",
      width: "18",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React48.createElement("g", { clipPath: "url(#clip0_4961_73)" }, /* @__PURE__ */ React48.createElement(
      "path",
      {
        d: "M9 5.375C10.3462 5.375 11.4375 4.28369 11.4375 2.9375C11.4375 1.59131 10.3462 0.5 9 0.5C7.65381 0.5 6.5625 1.59131 6.5625 2.9375C6.5625 4.28369 7.65381 5.375 9 5.375Z",
        fill: "#94A6B8"
      }
    ), /* @__PURE__ */ React48.createElement(
      "path",
      {
        d: "M9 11.9375C10.3462 11.9375 11.4375 10.8462 11.4375 9.5C11.4375 8.15381 10.3462 7.0625 9 7.0625C7.65381 7.0625 6.5625 8.15381 6.5625 9.5C6.5625 10.8462 7.65381 11.9375 9 11.9375Z",
        fill: "#94A6B8"
      }
    ), /* @__PURE__ */ React48.createElement(
      "path",
      {
        d: "M9 18.5C10.3462 18.5 11.4375 17.4087 11.4375 16.0625C11.4375 14.7163 10.3462 13.625 9 13.625C7.65381 13.625 6.5625 14.7163 6.5625 16.0625C6.5625 17.4087 7.65381 18.5 9 18.5Z",
        fill: "#94A6B8"
      }
    )),
    /* @__PURE__ */ React48.createElement("defs", null, /* @__PURE__ */ React48.createElement("clipPath", { id: "clip0_4961_73" }, /* @__PURE__ */ React48.createElement("rect", { fill: "white", height: "18", transform: "translate(0 0.5)", width: "18" })))
  )));
}

// src/components/individual-actions/individual-actions-body-component.tsx
import styled70 from "styled-components";
var IndividualActionsBodyComponent = styled70.div`
  padding: 5px;
  z-index: 100;
  background-color: var(--white);
  display: flex;
  flex-direction: column;
  width: 200px;
  border-radius: 10px;
  border: solid var(--grey100) 1px;
`;

// src/components/individual-actions/individual-action-item-component.tsx
import styled71, { css as css3 } from "styled-components";
var IndividualActionItemComponent = styled71.div`
  color: var(--grey800);
  padding: 5px 10px;
  cursor: pointer;

  &:first-of-type {
    border-radius: 5px 5px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 5px 5px;
  }

  &:hover {
    background-color: var(--grey100);
  }

  & + & {
    border-top: solid var(--grey100) 1px;
  }

  ${(props) => props.enabled !== void 0 && !props.enabled && css3`
      cursor: none;
      pointer-events: none;
      background-color: var(--grey100);
      color: var(--grey700);
    `}
`;

// src/components/monitor/event.ts
var eventLevels = {
  high: "critical",
  medium: "medium",
  low: "warning",
  info: "info"
};

// src/components/monitor/monitor-component.tsx
import React51, { useMemo as useMemo9, useState as useState20, useEffect as useEffect13 } from "react";
import styled73 from "styled-components";

// src/components/monitor/event-component.tsx
import React49 from "react";
import styled72 from "styled-components";
import { IconComponent as IconComponent4 } from "@ontruck/fe-icons";
var getBorderColor = (level) => {
  if (level === eventLevels.high) {
    return "5px solid var(--otk-theme-color-error)";
  }
  if (level === eventLevels.medium) {
    return "5px solid var(--otk-theme-color-warning)";
  }
  if (level === eventLevels.low) {
    return "5px solid var(--otk-theme-color-info)";
  }
  return "unset";
};
var EventRootComponent = styled72.article`
  display: flex;
  padding: var(--otk-theme-space-m) var(--otk-theme-space-m) 0 var(--otk-theme-space-m);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--otk-theme-space-s);
  align-self: stretch;
  background: var(--otk-theme-color-background-color-white, white);
  border-left: ${({ level }) => getBorderColor(level)};
`;
var EventHeaderComponent = styled72.header`
  display: flex;
  height: 24px;
  align-items: center;
  gap: var(--otk-theme-space-s);
  width: 100%;
`;
var EventActionAndActorComponent = styled72.div`
  display: flex;
  align-items: flex-start;
  gap: var(--otk-theme-space-xs);
  flex-wrap: wrap;
  width: 100%;
  flex: 1;
`;
var EventTagsComponent = styled72(FlexComponent)`
  align-items: flex-start;
  gap: var(--otk-theme-space-s);
`;
var EventWhoAndWhenComponent = styled72.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--otk-theme-space-xs);
`;
var EventBodyComponent = styled72.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--otk-theme-space-xs);
`;
var EventSeparatorComponent = styled72.div`
  width: 100%;
  height: 1px;
  background: var(--otk-theme-color-grey-300);
  margin-top: var(--otk-theme-space-s);
`;
function EventComponent({
  action,
  actor,
  icon,
  who,
  when,
  bodyComponent,
  tags,
  level
}) {
  var _a, _b;
  return /* @__PURE__ */ React49.createElement(EventRootComponent, { level }, /* @__PURE__ */ React49.createElement(EventHeaderComponent, null, /* @__PURE__ */ React49.createElement(EventActionAndActorComponent, null, icon ? /* @__PURE__ */ React49.createElement(IconComponent4, { color: icon.color, name: icon.name }) : null, /* @__PURE__ */ React49.createElement(
    TextComponent,
    {
      color: "var(--otk-theme-color-grey-900);",
      flexWrap: "wrap",
      fontSize: "s",
      fontWeight: "medium"
    },
    action
  ), actor ? /* @__PURE__ */ React49.createElement(
    TextComponent,
    {
      color: "var(--otk-theme-color-grey-700);",
      flexWrap: "wrap",
      fontSize: "s",
      fontWeight: "medium"
    },
    "/ ",
    actor
  ) : null), /* @__PURE__ */ React49.createElement(EventTagsComponent, null, tags ? /* @__PURE__ */ React49.createElement(FlexComponent, { flexWrap: "wrap", gap: "var(--otk-theme-space-s)" }, tags.map((tag) => /* @__PURE__ */ React49.createElement(
    BadgeComponent,
    {
      backgroundColor: "#e0eaff;",
      borderLeft: Boolean(level),
      borderLeftColor: level === eventLevels.high ? "var(--otk-theme-color-error)" : "",
      key: tag
    },
    tag
  ))) : null)), bodyComponent ? /* @__PURE__ */ React49.createElement(EventBodyComponent, null, bodyComponent) : null, /* @__PURE__ */ React49.createElement(EventWhoAndWhenComponent, null, who ? /* @__PURE__ */ React49.createElement(
    BadgeComponent,
    {
      backgroundColor: (_a = who.backgroundColor) != null ? _a : void 0,
      color: (_b = who.color) != null ? _b : void 0
    },
    who.label
  ) : null, (who == null ? void 0 : who.details) ? /* @__PURE__ */ React49.createElement(TextComponent, { color: "var(--otk-theme-color-grey-500);", fontSize: "s", fontWeight: "medium" }, who.details, " -") : null, /* @__PURE__ */ React49.createElement(TextComponent, { color: "var(--otk-theme-color-grey-500);", fontSize: "s", fontWeight: "medium" }, when)), /* @__PURE__ */ React49.createElement(EventSeparatorComponent, null));
}
EventComponent.Root = EventRootComponent;
EventComponent.Header = EventHeaderComponent;
EventComponent.ActionAndActor = EventActionAndActorComponent;
EventComponent.WhoAndWhen = EventWhoAndWhenComponent;
EventComponent.Body = EventBodyComponent;
EventComponent.Separator = EventSeparatorComponent;

// src/components/monitor/get-events-tags-service.ts
function getEventsTagsService(events) {
  if (!events) {
    return [];
  }
  const tags = /* @__PURE__ */ new Set();
  for (const event of events) {
    if (!event.tags || event.tags.length === 0) {
      continue;
    }
    for (const tag of event.tags) {
      if (tag && !tags.has(tag)) {
        tags.add(tag);
      }
    }
  }
  return Array.from(tags);
}

// src/components/monitor/event-tag-button-component.tsx
import React50 from "react";
function EventTagButtonComponent({
  tag,
  onClick,
  isSelected,
  tagDisplayName
}) {
  return /* @__PURE__ */ React50.createElement(
    ButtonComponent,
    {
      color: "primary",
      onClick: () => onClick == null ? void 0 : onClick(tag),
      size: "xs",
      variant: isSelected ? void 0 : "outline"
    },
    tagDisplayName != null ? tagDisplayName : tag
  );
}

// src/components/monitor/monitor-component.tsx
var MONITOR_HEADER_HEIGHT = "60px";
var MonitorRootComponent = styled73.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  flex-shrink: 0;
  border-left: ${({ hasBorderLeft }) => hasBorderLeft ? "1px solid var(--otk-theme-color-grey-200, #e2e8ee)" : "none"};
  background: var(--otk-theme-color-background-color-white, white);
  overflow-y: hidden;
`;
var MonitorHeaderComponent = styled73.header`
  display: flex;
  height: ${MONITOR_HEADER_HEIGHT};
  padding: var(--otk-theme-space-m) var(--otk-theme-space-m);
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  background: var(--otk-theme-color-grey-200, #e2e8ee);
`;
var MonitorTitleComponent = styled73.h3`
  color: var(--otk-theme-color-grey-700, #54708c);
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-family: var(--otk-theme-font-family-primary, Roboto);
  font-style: normal;
  font-size: var(--otk-theme-font-size-m, 16px);
  font-weight: var(--otk-theme-font-weight-l, 700);
  line-height: var(--otk-theme-line-height-m, 16px);
  text-transform: uppercase;
  margin: 0;
  white-space: 'nowrap';
  overflow: 'hidden';
  text-overflow: 'ellipsis';
`;
function MonitorComponent({
  className,
  events,
  title = "Logs",
  hiddenTags = [],
  isClosable = false,
  showVisibleTags = true,
  hasBorderLeft = true
}) {
  const eventsTags = getEventsTagsService(events);
  const visibleTags = eventsTags.filter(
    (tag) => !hiddenTags.some((hiddenTag) => hiddenTag.tagName === tag)
  );
  const invisibleTags = hiddenTags.map((hiddenTag) => hiddenTag.tagName);
  const [areTagsInitialized, setAreTagsInitialized] = useState20(false);
  const [selectedTags, setSelectedTags] = useState20([]);
  useEffect13(() => {
    if (events === void 0 || events.length === 0) {
      return;
    }
    if (areTagsInitialized) {
      return;
    }
    const allTagsSet = /* @__PURE__ */ new Set([...eventsTags, ...invisibleTags, ...visibleTags]);
    const allTags = Array.from(allTagsSet);
    setAreTagsInitialized(true);
    setSelectedTags(allTags);
  }, [eventsTags, invisibleTags, selectedTags, areTagsInitialized, events, visibleTags]);
  const handleTagButtonClick = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  const filteredEvents = useMemo9(() => {
    if (!events) {
      return [];
    }
    if (eventsTags.length === 0) {
      return events;
    }
    const eventsWithAllTagsSelected = events.filter((event) => {
      var _a;
      return (_a = event.tags) == null ? void 0 : _a.every((tag) => selectedTags.includes(tag));
    });
    return eventsWithAllTagsSelected;
  }, [events, selectedTags, eventsTags]);
  return /* @__PURE__ */ React51.createElement(MonitorRootComponent, { className, hasBorderLeft }, /* @__PURE__ */ React51.createElement(MonitorHeaderComponent, null, /* @__PURE__ */ React51.createElement(MonitorTitleComponent, null, title), showVisibleTags ? /* @__PURE__ */ React51.createElement(
    FlexComponent,
    {
      flexDirection: "row",
      gap: "var(--otk-theme-space-s)",
      marginRight: isClosable ? "var(--otk-theme-space-xl)" : "0"
    },
    visibleTags.length > 0 ? visibleTags.map((tag) => {
      return /* @__PURE__ */ React51.createElement(
        EventTagButtonComponent,
        {
          isSelected: selectedTags.includes(tag),
          key: tag,
          onClick: handleTagButtonClick,
          tag
        }
      );
    }) : null
  ) : null), invisibleTags.length > 0 ? /* @__PURE__ */ React51.createElement(MonitorHeaderComponent, null, /* @__PURE__ */ React51.createElement(FlexComponent, { flexDirection: "row", gap: "var(--otk-theme-space-s)" }, invisibleTags.length > 0 ? invisibleTags.map((tag) => {
    var _a;
    return /* @__PURE__ */ React51.createElement(
      EventTagButtonComponent,
      {
        isSelected: selectedTags.includes(tag),
        key: tag,
        onClick: handleTagButtonClick,
        tag,
        tagDisplayName: (_a = hiddenTags.find((hiddenTag) => hiddenTag.tagName === tag)) == null ? void 0 : _a.tagDisplayName
      }
    );
  }) : null)) : null, filteredEvents.length > 0 ? /* @__PURE__ */ React51.createElement(
    FlexComponent,
    {
      flexDirection: "column",
      height: `calc(100vh - ${MONITOR_HEADER_HEIGHT})`,
      overflowY: "auto",
      width: "100%"
    },
    filteredEvents.map((event) => {
      var _a, _b, _c;
      const tags = (_a = event.tags) == null ? void 0 : _a.filter(
        (tag) => !hiddenTags.some((hiddenTag) => hiddenTag.tagName === tag)
      );
      return /* @__PURE__ */ React51.createElement(
        EventComponent,
        {
          action: event.action,
          actor: (_b = event.actor) != null ? _b : null,
          bodyComponent: (_c = event.bodyComponent) != null ? _c : null,
          icon: event.icon,
          key: event.id,
          level: event.level,
          tags,
          when: event.when,
          who: event.who
        }
      );
    })
  ) : null);
}
MonitorComponent.Root = MonitorRootComponent;
MonitorComponent.Header = MonitorHeaderComponent;
MonitorComponent.Title = MonitorTitleComponent;
MonitorComponent.Event = EventComponent;
MonitorComponent.TagButton = EventTagButtonComponent;

// src/components/tabs/horizontal-tabs-component.tsx
import React54, { useState as useState21 } from "react";
import styled75 from "styled-components";

// src/components/tabs/horizontal-tab-component.tsx
import React52 from "react";
import styled74 from "styled-components";
var HORIZONTAL_TAB_HEIGHT_IN_PIXELS = 64;
var HorizontalTabRootComponent = styled74.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--otk-ui-tabs-tab-gap);
  padding: var(--otk-ui-tabs-tab-padding);
  background-color: var(--otk-ui-tabs-tab-background-color);
  border: none;
  border-left: var(--otk-ui-tabs-tab-border-left);
  border-bottom: ${({ isActive }) => isActive ? "var(--otk-ui-tabs-tab-is-active-border-bottom)" : "var(--otk-ui-tabs-tab-border-bottom)"};
  width: 100%;
  height: ${HORIZONTAL_TAB_HEIGHT_IN_PIXELS}px;
  flex: 1;

  &:hover {
    cursor: pointer;
  }

  &:first-child {
    border-left: none;
  }
`;
function HorizontalTabComponent({
  id,
  children,
  label,
  tabComponent,
  uppercase = false,
  onClick,
  markActive = false,
  isActive = false
}) {
  if (tabComponent) {
    return /* @__PURE__ */ React52.createElement(React52.Fragment, null, tabComponent);
  }
  const handleClick = (tabId) => {
    if (onClick) {
      onClick(tabId);
    }
  };
  return /* @__PURE__ */ React52.createElement(
    HorizontalTabRootComponent,
    {
      isActive: isActive ? markActive : void 0,
      onClick: () => {
        handleClick(id);
      },
      type: "button"
    },
    children ? children : null,
    label ? /* @__PURE__ */ React52.createElement(TextComponent, { fontWeight: "medium", uppercase }, label) : null
  );
}

// src/components/tabs/horizontal-tabs-content-component.tsx
import React53 from "react";
function HorizontalTabsContentComponent({
  children
}) {
  return /* @__PURE__ */ React53.createElement(FlexComponent, { flexDirection: "column", width: "100%" }, children);
}

// src/components/tabs/horizontal-tabs-component.tsx
var HorizontalTabContentRootComponent = styled75.div`
  overflow-y: auto;
  width: 100%;
  height: ${({ maxHeight }) => `calc(${maxHeight} - ${HORIZONTAL_TAB_HEIGHT_IN_PIXELS}px)`};
`;
function HorizontalTabsComponent({
  tabs,
  uppercase = false,
  markActive = false,
  maxContentHeight = "100%"
}) {
  const [activeTab, setActiveTab] = useState21(tabs.length > 0 ? tabs[0].id : "");
  const handleTabClick = (id) => {
    setActiveTab(id);
  };
  return /* @__PURE__ */ React54.createElement(FlexComponent, { flexDirection: "column", width: "100%" }, /* @__PURE__ */ React54.createElement(FlexComponent, { flexDirection: "row", width: "100%" }, tabs.map((tab) => /* @__PURE__ */ React54.createElement(
    HorizontalTabComponent,
    {
      id: tab.id,
      isActive: activeTab === tab.id,
      key: tab.id,
      label: tab.label,
      markActive,
      onClick: handleTabClick,
      uppercase
    },
    tab.tabComponent
  ))), /* @__PURE__ */ React54.createElement(HorizontalTabsContentComponent, null, tabs.map(
    (tab) => activeTab === tab.id ? /* @__PURE__ */ React54.createElement(HorizontalTabContentRootComponent, { key: tab.label, maxHeight: maxContentHeight }, tab.contentComponent) : null
  )));
}
HorizontalTabsComponent.Tab = HorizontalTabComponent;
HorizontalTabsComponent.Content = HorizontalTabsContentComponent;
export {
  AccordionComponent,
  AccordionContentComponent,
  AccordionIconComponent,
  AccordionTriggerComponent,
  AvatarComponent,
  BadgeComponent,
  BallComponent,
  Block,
  BlockContainer,
  BoxesSpinnerComponent,
  ButtonComponent,
  ButtonContainer,
  ButtonGroupComponent,
  CheckboxInput as CheckboxInputComponent,
  CloseButtonContainer,
  DRAWER_REGION_ID_LEFT,
  DRAWER_REGION_ID_RIGHT,
  DateInput as DateInputComponent,
  DrawerComponent,
  DropdownContainer,
  DropdownItemContainer,
  ErrorMessage,
  EventComponent,
  FlexComponent,
  FormContainer,
  Half,
  HorizontalTabsComponent,
  IndividualActionItemComponent,
  IndividualActionsBodyComponent,
  IndividualActionsMenuButtonComponent,
  Input,
  LabelledBallComponent,
  ModalComponent,
  ModalDescriptionComponent,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalRootComponent,
  ModalSectionComponent,
  ModalTitleComponent,
  MonitorComponent,
  MultiSelectInputComponent,
  PageContent,
  PageLayout,
  PhoneInput,
  Popover,
  PremiumInputContainer,
  RadioInputComponent,
  RadiosInputComponent,
  SectionBody,
  SectionContainer,
  SectionLine,
  SectionTitle,
  SelectInputComponent,
  SelectWithInternalSearchInputComponent,
  SemaphoreInputComponent,
  SpinnerComponent,
  Sticky,
  TableCellComponent,
  TableComponent,
  TableErrorComponent,
  TablePaginationComponent,
  TableRowComponent,
  TableStickyComponent,
  TextComponent,
  TextInput as TextInputComponent,
  TextsComponent,
  TimeInputNativeComponent,
  ToolbarButtonComponent,
  ToolbarComponent,
  ToolbarContentComponent,
  TooltipBodyComponent,
  TooltipComponent,
  TooltipHeaderComponent,
  TooltipUnorderedListComponent,
  buttonColors,
  buttonSizes,
  buttonVariants,
  eventLevels,
  getAvatarBackgroundByUserService,
  getAvatarInitialsByUserService,
  inputTypes,
  persistentStorageService,
  spinnerSizes,
  spinnerVariants,
  tableHeaderColumnAlignments,
  textSizes,
  textWeights,
  useDebounceHook,
  useDisableInputScrollHook,
  useFocusHook,
  useKeyPressHook,
  useOutsideClickHook,
  useScrollCacheHook
};
